<template>
    <div
        class="baby-album-page"
        :class="{
            'native-nav-page': useNativeAlbumNav,
            'wechat-browser-page': isWechatBrowser,
            'search-page-shell': isSearchSubpage
        }"
        @touchstart.capture="handleAlbumTouchStart"
        @touchmove.capture="handleAlbumTouchMove"
        @touchend.capture="handleAlbumTouchEnd"
        @touchcancel.capture="handleAlbumTouchEnd"
        @click.capture="captureAlbumPointer"
        @click="closeMenu"
    >
        <section
            v-if="shouldRenderHomeStickyShell"
            ref="stickyShell"
            class="album-sticky-shell album-home-shell"
            :class="{ 'home-chrome-collapsed': isHomeChromeCollapsed }"
            @click.stop
        >
            <header
                v-if="!shouldUseNativeRootNav"
                class="album-header home-header"
                :class="{ 'wechat-browser-header': isWechatBrowser }"
            >
                <button type="button" class="header-side header-back" @click="goBack">
                    <span class="header-back-icon" aria-hidden="true"></span>
                    <span class="header-back-text">返回</span>
                </button>
                <div class="header-center">
                    <div class="header-title">宝宝相册</div>
                </div>
                <div class="header-actions">
                    <button
                        v-if="isHomeChromeCollapsed"
                        type="button"
                        class="header-search-compact"
                        aria-label="搜索"
                        @click="openSearchHub"
                    >
                        ⌕
                    </button>
                    <button type="button" class="header-camera header-plus-icon" aria-label="新增" @click="openCreatePanel">+</button>
                </div>
            </header>

            <div class="home-collapsible-panel">
                <button type="button" class="search-entry-card" @click="openSearchHub">
                    <span class="search-entry-icon" aria-hidden="true">⌕</span>
                    <span class="search-entry-placeholder">输入标签或关键词</span>
                    <span class="search-entry-hint">搜索日期、照片、视频</span>
                </button>

                <section class="home-overview-panel">
                    <div class="overview-stat">
                        <strong>{{ albumDayCount }}</strong>
                        <span>有内容天数</span>
                    </div>
                    <div class="overview-stat">
                        <strong>{{ albumPhotoCount }}</strong>
                        <span>照片</span>
                    </div>
                    <div class="overview-stat">
                        <strong>{{ albumVideoCount }}</strong>
                        <span>视频</span>
                    </div>
                    <button
                        v-if="currentDate !== 'all'"
                        type="button"
                        class="overview-filter-chip"
                        @click="clearDateFilter"
                    >
                        正在查看 {{ selectedDateLabel }}
                    </button>
                    <div v-else class="overview-summary-copy">
                        最近的成长记录会按时间倒序留在这里，搜索页负责分流到日期、照片和视频视图。
                    </div>
                </section>
            </div>

            <section v-if="activeUploadSummary" class="upload-progress-panel">
                <div class="upload-progress-head">
                    <strong>{{ uploadProgressTitle }}</strong>
                    <span class="upload-progress-count">{{ uploadProgressCountLabel }}</span>
                </div>
                <div class="progress-track upload-progress-track">
                    <div class="progress-bar upload-progress-bar" :style="{ width: `${activeUploadSummary.progressPercent}%` }"></div>
                </div>
                <p class="upload-progress-note">照片或视频上传中，切勿退出APP。</p>
            </section>
        </section>
        <div v-if="isAlbumRootPage" class="album-sticky-spacer" :style="{ height: `${stickyShellHeight}px` }"></div>

        <section v-if="shouldShowInitialLoading" class="status-state">
            <div class="status-title">正在同步宝宝相册</div>
            <p>正在读取服务端相册记录，请稍候。</p>
        </section>

        <section v-else-if="loadError" class="status-state error-state">
            <div class="status-title">相册加载失败</div>
            <p>{{ loadError }}</p>
            <button type="button" class="empty-action" @click="loadAlbumGroups">重新加载</button>
        </section>

        <template v-else-if="isSearchHubPage">
            <section class="subpage-shell search-hub-page" @click.stop>
                <header class="subpage-header">
                    <button type="button" class="header-side header-back compact-back" @click="goBack">
                        <span class="header-back-icon" aria-hidden="true"></span>
                    </button>
                    <div class="header-center">
                        <div class="header-title">搜索成长瞬间</div>
                    </div>
                    <button
                        v-if="searchHubKeyword"
                        type="button"
                        class="search-reset-button"
                        @click="clearSearchHub"
                    >
                        清空
                    </button>
                    <div v-else class="header-actions placeholder-actions"></div>
                </header>

                <section class="search-hero-panel">
                    <div class="search-hero-copy">
                        <span class="search-hero-kicker">BABY ALBUM</span>
                        <strong>按时间、照片和视频找回每个片段</strong>
                        <p>输入地点、文案或标签后，下方入口会同步筛选结果。</p>
                    </div>
                    <label class="search-hub-input-shell">
                        <span class="search-hub-search-icon" aria-hidden="true">⌕</span>
                        <input
                            v-model.trim="searchHubKeyword"
                            type="search"
                            class="search-hub-input"
                            placeholder="搜索日期、地点、标签或文案"
                            @keyup.enter="openPhotoExplorer"
                        >
                        <button
                            v-if="searchHubKeyword"
                            type="button"
                            class="search-inline-clear"
                            aria-label="清空搜索"
                            @click.prevent="clearSearchHub"
                        >×</button>
                    </label>
                </section>

                <div class="search-hub-shortcuts">
                    <button
                        v-for="card in searchHubCards"
                        :key="card.key"
                        type="button"
                        class="search-hub-shortcut"
                        :class="`search-hub-shortcut--${card.key}`"
                        @click="handleSearchHubSelection(card.key)"
                    >
                        <span class="search-shortcut-icon" aria-hidden="true">{{ card.icon }}</span>
                        <span class="search-shortcut-copy">
                            <span class="search-hub-shortcut-label">{{ card.label }}</span>
                            <strong class="search-hub-shortcut-metric">{{ card.metric }}</strong>
                            <small>{{ card.note }}</small>
                        </span>
                        <span class="search-shortcut-arrow" aria-hidden="true">›</span>
                    </button>
                </div>

                <section class="search-context-panel">
                    <div class="search-context-item">
                        <span>范围</span>
                        <strong>{{ searchHubKeyword ? '已按关键词筛选' : '全部宝宝相册' }}</strong>
                    </div>
                    <div class="search-context-item">
                        <span>建议</span>
                        <strong>{{ searchHubKeyword ? '优先查看照片结果' : '先从日期入口快速定位' }}</strong>
                    </div>
                </section>
            </section>
        </template>

        <template v-else-if="isDateExplorerPage && !isDateResultGridPage">
            <section class="subpage-shell date-explorer-page" @click.stop>
                <header class="subpage-header">
                    <button type="button" class="header-side header-back compact-back" @click="goBack">
                        <span class="header-back-icon" aria-hidden="true"></span>
                    </button>
                    <div class="header-center">
                        <div class="header-title">{{ pageTitle }}</div>
                    </div>
                    <div class="header-actions placeholder-actions"></div>
                </header>

                <div class="calendar-weekdays">
                    <span v-for="weekday in weekdays" :key="weekday">{{ weekday }}</span>
                </div>

                <section
                    v-for="month in calendarMonths"
                    :key="month.id"
                    class="calendar-month-section"
                >
                    <div class="calendar-month-title">{{ month.label }}</div>
                    <div class="calendar-grid">
                        <div
                            v-for="cell in month.days"
                            :key="cell.key"
                            class="calendar-cell"
                            :class="{ blank: cell.blank }"
                        >
                            <button
                                v-if="!cell.blank"
                                type="button"
                                class="calendar-day"
                                :class="{
                                    active: cell.hasEntries,
                                    today: cell.isToday,
                                    selected: cell.isSelected
                                }"
                                @click="selectCalendarDay(cell)"
                            >
                                <span>{{ cell.day }}</span>
                                <small v-if="cell.isToday">今天</small>
                            </button>
                        </div>
                    </div>
                </section>
            </section>
        </template>

        <template v-else-if="isSearchResultGridPage">
            <section class="subpage-shell media-grid-page" @click.stop>
                <header class="subpage-header">
                    <button type="button" class="header-side header-back compact-back" @click="goBack">
                        <span class="header-back-icon" aria-hidden="true"></span>
                    </button>
                    <div class="header-center">
                        <div class="header-title">{{ pageTitle }}</div>
                    </div>
                    <div class="header-actions media-header-actions">
                        <button
                            v-if="batchDeleteMode"
                            type="button"
                            class="header-side header-search media-header-text"
                            @click="cancelBatchDeleteMode"
                        >
                            完成
                        </button>
                        <button
                            v-else
                            type="button"
                            class="media-more-button"
                            aria-label="更多操作"
                            @click="openMediaActionSheet"
                        >
                            ···
                        </button>
                    </div>
                </header>

                <section v-if="activeUploadSummary" class="upload-progress-panel inline-upload-panel">
                    <div class="upload-progress-head">
                        <strong>{{ uploadProgressTitle }}</strong>
                        <span class="upload-progress-count">{{ uploadProgressCountLabel }}</span>
                    </div>
                    <div class="progress-track upload-progress-track">
                        <div class="progress-bar upload-progress-bar" :style="{ width: `${activeUploadSummary.progressPercent}%` }"></div>
                    </div>
                    <p class="upload-progress-note">照片或视频上传中，切勿退出APP。</p>
                </section>

                <section v-if="!currentMediaSections.length" class="status-state media-empty-state">
                    <div class="status-title">{{ mediaEmptyTitle }}</div>
                    <p>{{ mediaEmptyDescription }}</p>
                    <button type="button" class="empty-action" @click="handleMediaEmptyAction">{{ mediaEmptyActionText }}</button>
                </section>

                <section
                    v-for="section in currentMediaSections"
                    v-else
                    :key="section.id"
                    class="media-section"
                >
                    <div class="media-section-title">{{ section.label }}</div>
                    <div class="media-grid">
                        <button
                            v-for="item in section.items"
                            :key="item.id"
                            type="button"
                            class="media-grid-item"
                            :class="{
                                selected: isMediaSelected(item.id),
                                'batch-mode-item': batchDeleteMode
                            }"
                            @click="handleMediaItemTap(item)"
                        >
                            <img
                                class="media-grid-image"
                                :class="{ loaded: isMediaThumbnailLoaded(item.id) }"
                                :src="item.thumbnail"
                                :alt="item.caption || `${item.postType}-${item.id}`"
                                loading="lazy"
                                decoding="async"
                                @load="markMediaThumbnailLoaded(item.id)"
                                @error="handleMediaThumbnailError($event, item)"
                            >
                            <span v-if="item.postType === 'video'" class="media-play-indicator" aria-hidden="true">▶</span>
                            <span v-if="item.postType === 'video'" class="media-duration">{{ item.videoDuration }}</span>
                            <span v-if="batchDeleteMode" class="media-select-indicator">
                                {{ isMediaSelected(item.id) ? '✓' : '' }}
                            </span>
                        </button>
                    </div>
                </section>
            </section>
        </template>

        <template v-else>
            <section v-if="isPrototypeMode" class="mode-banner" @click.stop>
                未检测到 token，当前为演示模式；发布、编辑、删除仅在当前页面生效，不会写入服务端。
            </section>

            <section v-if="!filteredGroups.length" class="empty-state">
                <div class="empty-icon">{{ currentDate === 'all' ? '📷' : '🗓️' }}</div>
                <h2>{{ currentDate === 'all' ? '开启记录宝宝的美好时光' : '这一天还没有记录' }}</h2>
                <p>{{ currentDate === 'all' ? '先发布一组照片或视频，时间轴就会从今天开始长出来。' : '返回全部时间，继续查看其它日期的成长记录。' }}</p>
                <button
                    type="button"
                    class="empty-action"
                    @click="currentDate === 'all' ? openCreatePanel() : clearDateFilter()"
                >
                    {{ currentDate === 'all' ? '开始记录' : '查看全部时间' }}
                </button>
            </section>

            <section
                v-for="group in filteredGroups"
                :key="group.id"
                class="album-group"
                @click.stop
            >
                <div class="group-header">
                    <span class="group-dot"></span>
                    <div class="group-title">
                        <strong>{{ group.label }}</strong>
                        <span>{{ group.ageText }}</span>
                    </div>
                </div>

                <div
                    v-for="(entry, entryIndex) in group.entries"
                    :key="entry.id"
                    class="album-card-wrap"
                >
                    <baby-album-card
                        :entry="entry"
                        :menu-visible="activeMenuId === entry.id"
                        :priority="filteredGroups.length > 0 && group.id === filteredGroups[0].id && entryIndex < 2"
                        @preview="previewEntry"
                        @toggle-menu="toggleMenu"
                        @debug="postAlbumDebug"
                        @edit="openEdit"
                        @remove="removeEntry"
                    />
                </div>
            </section>
        </template>

        <transition name="sheet-fade">
            <div v-if="showCreatePanel && !activeComposer" class="overlay-mask" @click="closeCreateFlow">
                <section class="bottom-sheet create-sheet" @click.stop>
                    <div class="sheet-handle"></div>
                    <div class="sheet-head">
                        <div>
                            <h3>记录这一刻</h3>
                            <p>选择最适合当下场景的记录方式，把宝宝成长片段整理进时间轴。</p>
                        </div>
                        <button type="button" class="sheet-close" @click="closeCreateFlow">关闭</button>
                    </div>
                    <section class="create-hero">
                        <div class="create-hero-copy">
                            <span class="create-hero-kicker">宝宝相册</span>
                            <strong>从拍摄、补录到智能筛选，一次完成</strong>
                            <p>拍摄适合即刻记录，照片视频适合手动挑选，智能上传适合批量整理系统相册。</p>
                        </div>
                    </section>
                    <div class="create-grid">
                        <button
                            v-for="item in creatorItems"
                            :key="item.key"
                            type="button"
                            class="create-item"
                            :class="`create-item--${item.key}`"
                            @click="openComposer(item.key)"
                        >
                            <span class="create-icon-shell">
                                <span class="create-icon" :style="{ background: item.color }">{{ item.symbol }}</span>
                            </span>
                            <div class="create-copy">
                                <strong>{{ item.label }}</strong>
                                <span>{{ item.description }}</span>
                            </div>
                            <span class="create-arrow" aria-hidden="true">›</span>
                        </button>
                    </div>
                </section>
            </div>
        </transition>

        <transition name="sheet-fade">
            <div v-if="activeComposer" class="overlay-mask" @click="closeCreateFlow">
                <section class="bottom-sheet composer-sheet" @click.stop>
                    <div class="sheet-head">
                        <div>
                            <h3>{{ composerTitle }}</h3>
                            <p>{{ composerSubtitle }}</p>
                        </div>
                        <button type="button" class="sheet-close" @click="handleComposerCancel">取消</button>
                    </div>

                    <template v-if="activeComposer === 'photo'">
                        <label class="secondary-action file-picker-action">
                            选择图片或视频文件
                            <input
                                class="composer-file-input composer-file-input--cover"
                                type="file"
                                :accept="composerInputAccept"
                                :multiple="composerAllowsMultiple"
                                @click.stop="notifyNativePickerMode('library')"
                                @change="handleLibraryInputChange"
                            >
                        </label>
                        <div v-if="composerHasAssets" class="sample-grid">
                            <div
                                v-for="asset in composerAssets"
                                :key="asset.id"
                                class="sample-image media-preview"
                            >
                                <img
                                    v-if="asset.kind === 'photo' || asset.thumbnailUrl"
                                    :src="asset.kind === 'photo' ? asset.previewUrl : asset.thumbnailUrl"
                                    :alt="asset.file.name"
                                    loading="lazy"
                                    decoding="async"
                                >
                                <div v-else class="video-fallback">视频</div>
                                <span v-if="asset.kind === 'video'" class="sample-badge">{{ asset.durationText }}</span>
                                <button type="button" class="asset-remove" @click.stop="removeComposerAsset(asset.id)">删除</button>
                            </div>
                        </div>
                        <div v-else class="asset-placeholder">
                            <strong>还没有选择素材</strong>
                            <span>支持一次选择多张照片，或选择 1 段视频。</span>
                        </div>
                        <textarea
                            v-model.trim="composerForm.caption"
                            class="composer-textarea"
                            rows="3"
                            placeholder="写一句当下的小记录"
                        ></textarea>
                        <input
                            v-model.trim="composerForm.location"
                            type="text"
                            class="composer-input"
                            placeholder="记录地点"
                        >
                        <button type="button" class="primary-action" :disabled="!canSubmitComposer" @click="submitComposer">
                            {{ isSubmittingComposer ? '加入上传队列中...' : '发布到时间轴' }}
                        </button>
                    </template>

                    <template v-else-if="activeComposer === 'camera'">
                        <div class="composer-tabs">
                            <button
                                type="button"
                                class="tab-chip"
                                :class="{ active: composerForm.mediaType === 'photo' }"
                                @click="selectComposerMediaType('photo')"
                            >
                                照片
                            </button>
                            <button
                                type="button"
                                class="tab-chip"
                                :class="{ active: composerForm.mediaType === 'video' }"
                                @click="selectComposerMediaType('video')"
                            >
                                视频
                            </button>
                        </div>
                        <label class="secondary-action file-picker-action">
                            {{ composerPickerLabel }}
                            <input
                                class="composer-file-input composer-file-input--cover"
                                type="file"
                                :accept="composerInputAccept"
                                :multiple="composerAllowsMultiple"
                                capture="environment"
                                @click.stop="notifyNativePickerMode('camera')"
                                @change="handleCameraInputChange"
                            >
                        </label>
                        <div v-if="composerHasAssets" class="sample-grid">
                            <div
                                v-for="asset in composerAssets"
                                :key="asset.id"
                                class="sample-image media-preview"
                            >
                                <img
                                    v-if="asset.kind === 'photo' || asset.thumbnailUrl"
                                    :src="asset.kind === 'photo' ? asset.previewUrl : asset.thumbnailUrl"
                                    :alt="asset.file.name"
                                    loading="lazy"
                                    decoding="async"
                                >
                                <div v-else class="video-fallback">视频</div>
                                <span v-if="asset.kind === 'video'" class="sample-badge">{{ asset.durationText }}</span>
                                <button type="button" class="asset-remove" @click.stop="removeComposerAsset(asset.id)">删除</button>
                            </div>
                        </div>
                        <div v-else class="asset-placeholder">
                            <strong>还没有选择素材</strong>
                            <span>支持真实图片上传；切到视频后可直接选择或录制视频。</span>
                        </div>
                        <textarea
                            v-model.trim="composerForm.caption"
                            class="composer-textarea"
                            rows="3"
                            placeholder="写一句当下的小记录"
                        ></textarea>
                        <input
                            v-model.trim="composerForm.location"
                            type="text"
                            class="composer-input"
                            placeholder="记录地点"
                        >
                        <button type="button" class="primary-action" :disabled="!canSubmitComposer" @click="submitComposer">
                            {{ isSubmittingComposer ? '加入上传队列中...' : '发布到时间轴' }}
                        </button>
                    </template>

                    <template v-else-if="activeComposer === 'smart-upload'">
                        <section class="smart-upload-shell">
                            <div class="smart-upload-hero">
                                <div class="smart-upload-copy">
                                    <span class="smart-upload-kicker">智能上传</span>
                                    <strong>先筛选，再导入</strong>
                                    <p>从系统相册批量选择后，系统会先按清晰度、时长和重复度整理候选素材，再由你确认导入。</p>
                                </div>
                                <label class="smart-upload-trigger file-picker-action">
                                    选择候选素材
                                    <input
                                        class="composer-file-input composer-file-input--cover"
                                        type="file"
                                        :accept="composerInputAccept"
                                        multiple
                                        @click.stop="notifyNativePickerMode('library')"
                                        @change="handleLibraryInputChange"
                                    >
                                </label>
                            </div>

                            <div class="smart-upload-stats">
                                <div class="smart-stat-card">
                                    <span>已分析</span>
                                    <strong>{{ smartUploadSummary.sourceCount || smartCandidates.length }}</strong>
                                </div>
                                <div class="smart-stat-card">
                                    <span>推荐导入</span>
                                    <strong>{{ recommendedSmartCandidateCount }}</strong>
                                </div>
                                <div class="smart-stat-card">
                                    <span>待复核</span>
                                    <strong>{{ reviewSmartCandidateCount }}</strong>
                                </div>
                            </div>

                            <div class="candidate-summary smart-summary-card">
                                {{ smartCandidates.length ? smartCandidateSummaryText : '先从系统相册选择图片或视频，我会按尺寸、时长和重复文件自动筛选并给出推荐。' }}
                            </div>

                            <div v-if="smartCandidates.length" class="smart-selection-toolbar">
                                <div class="smart-selection-count">已选 {{ selectedCandidateCount }} 条</div>
                                <div class="smart-selection-actions">
                                    <button type="button" class="smart-selection-pill" @click="selectRecommendedSmartCandidates">全选推荐</button>
                                    <button type="button" class="smart-selection-pill muted" @click="clearSmartCandidateSelection">清空选择</button>
                                </div>
                            </div>

                            <div v-if="smartCandidates.length" class="candidate-list smart-candidate-list">
                                <button
                                    v-for="candidate in smartCandidates"
                                    :key="candidate.id"
                                    type="button"
                                    class="candidate-card smart-candidate-card"
                                    :class="{
                                        selected: candidate.selected,
                                        recommended: candidate.qualityLabel === '推荐导入'
                                    }"
                                    @click="toggleSmartCandidateSelection(candidate.id)"
                                >
                                    <div class="candidate-media">
                                        <img :src="candidate.previewUrl" :alt="candidate.title" loading="lazy" decoding="async">
                                        <span class="candidate-select-chip">{{ candidate.selected ? '已选中' : '点按选择' }}</span>
                                        <span class="candidate-type-chip" :class="{ recommended: candidate.qualityLabel === '推荐导入' }">
                                            {{ candidate.qualityLabel }}
                                        </span>
                                        <span v-if="candidate.mediaType === 'video'" class="sample-badge">{{ candidate.durationText }}</span>
                                    </div>
                                    <div class="candidate-copy">
                                        <div class="candidate-title-row">
                                            <strong>{{ candidate.title }}</strong>
                                            <span class="candidate-media-tag">{{ candidate.mediaType === 'video' ? '视频' : '照片' }}</span>
                                        </div>
                                        <span>{{ candidate.takenAtLabel }} · {{ candidate.location }}</span>
                                        <span>{{ candidate.source }}</span>
                                        <span class="candidate-reason-badge">{{ candidate.reasonText }}</span>
                                        <span v-if="candidate.duplicateCount" class="candidate-duplicate-note">已自动合并 {{ candidate.duplicateCount }} 个重复文件</span>
                                    </div>
                                </button>
                            </div>
                            <div v-else class="asset-placeholder smart-empty-state">
                                <strong>还没有候选素材</strong>
                                <span>支持从系统相册一次选择多张图片或视频，自动去重、排序并给出推荐导入结果。</span>
                            </div>
                        </section>
                        <button
                            type="button"
                            class="primary-action"
                            :disabled="!canSubmitComposer"
                            @click="importSmartCandidates"
                        >
                            {{ isSubmittingComposer ? '加入上传队列中...' : `导入 ${selectedCandidateCount} 条素材` }}
                        </button>
                    </template>

                    <template v-else-if="activeComposer === 'edit' && editingEntry">
                        <button type="button" class="secondary-action compact" @click="openEditTimePicker">
                            记录时间：{{ formattedEditTakenAt }}
                        </button>
                        <div class="sort-panel">
                            <div class="sort-head">
                                <strong>媒体顺序</strong>
                                <span>{{ editMediaItems.length > 1 ? '调整后会按新顺序展示在卡片中。' : '当前只有 1 个素材，无需调整顺序。' }}</span>
                            </div>
                            <div class="sort-grid">
                                <div
                                    v-for="(item, index) in editMediaItems"
                                    :key="item.id"
                                    class="sort-item"
                                >
                                    <div class="sort-media">
                                        <img :src="item.previewUrl" :alt="item.alt" loading="lazy" decoding="async">
                                        <span class="sort-index">{{ index + 1 }}</span>
                                        <span v-if="item.badge" class="sample-badge">{{ item.badge }}</span>
                                    </div>
                                    <div class="sort-actions">
                                        <button type="button" class="sort-button" :disabled="index === 0" @click="moveEditAsset(index, -1)">前移</button>
                                        <button type="button" class="sort-button" :disabled="index === editMediaItems.length - 1" @click="moveEditAsset(index, 1)">后移</button>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <textarea
                            v-model.trim="editDraft.caption"
                            class="composer-textarea"
                            rows="3"
                            placeholder="补充文案"
                        ></textarea>
                        <input
                            v-model.trim="editDraft.location"
                            type="text"
                            class="composer-input"
                            placeholder="记录地点"
                        >
                        <button type="button" class="primary-action" :disabled="isSubmittingComposer" @click="saveEdit">{{ isSubmittingComposer ? '保存中...' : '保存修改' }}</button>
                    </template>
                </section>
            </div>
        </transition>

        <van-popup
            v-model="showEditTimePicker"
            position="bottom"
        >
            <van-datetime-picker
                v-model="editPickerDate"
                type="datetime"
                title="选择记录时间"
                :min-date="editMinDate"
                :max-date="editMaxDate"
                @confirm="handleEditTimeConfirm"
                @cancel="handleEditTimeCancel"
            />
        </van-popup>

        <transition name="sheet-fade">
            <div v-if="showMediaActionSheet" class="overlay-mask" @click="closeMediaActionSheet">
                <section class="bottom-sheet media-action-sheet" @click.stop>
                    <button type="button" class="sheet-action-button danger" @click="enterBatchDeleteMode">批量删除</button>
                    <button type="button" class="sheet-action-button cancel" @click="closeMediaActionSheet">取消</button>
                </section>
            </div>
        </transition>

        <transition name="sheet-fade">
            <div v-if="batchDeleteMode" class="batch-delete-shell" @click.stop>
                <div class="batch-delete-bar">
                    <div class="batch-delete-copy">
                        <strong>已选择 {{ selectedMediaCount }} 项</strong>
                        <span>确认后会立即从宝宝相册中移除已选{{ currentMediaTypeLabel }}</span>
                    </div>
                    <div class="batch-delete-actions">
                        <button type="button" class="batch-secondary" @click="cancelBatchDeleteMode">取消</button>
                        <button type="button" class="batch-primary" :disabled="!selectedMediaCount || batchDeletePending" @click="confirmBatchDelete">
                            {{ batchDeletePending ? '删除中...' : '删除所选' }}
                        </button>
                    </div>
                </div>
            </div>
        </transition>

        <transition name="sheet-fade">
            <div v-if="videoPreviewEntry" class="overlay-mask video-mask">
                <section class="video-preview" @click.stop>
                    <button type="button" class="video-close" @click="closeVideoPreview">关闭</button>
                    <video
                        v-if="videoPreviewEntry.videoUrl"
                        ref="videoPreviewEl"
                        class="video-element"
                        :src="activeVideoPreviewUrl"
                        :poster="videoPreviewPoster"
                        controls
                        autoplay
                        playsinline
                        webkit-playsinline
                        preload="auto"
                        @click.stop
                        @touchstart.stop
                        @touchend.stop
                        @loadedmetadata="playVideoPreview"
                        @canplay="playVideoPreview"
                        @error="handleVideoPreviewError"
                    ></video>
                    <button
                        v-if="videoPreviewEntry.videoUrl && showVideoPreviewPlayButton"
                        type="button"
                        class="video-preview-play"
                        @click.stop="playVideoPreview"
                        @touchend.stop.prevent="playVideoPreview"
                    >▶</button>
                    <template v-if="!videoPreviewEntry.videoUrl">
                        <img class="video-preview-poster" :src="videoPreviewPoster" :alt="videoPreviewEntry.caption || '视频封面'">
                        <button type="button" class="video-play" @click="mockPlayVideo">▶</button>
                    </template>
                    <span class="video-length">{{ videoPreviewEntry.videoDuration || '00:00' }}</span>
                    <div class="video-copy">
                        <strong>{{ videoPreviewEntry.caption || '视频记录' }}</strong>
                        <span>{{ videoPreviewEntry.location || '宝宝相册' }}</span>
                    </div>
                </section>
            </div>
        </transition>

    </div>
</template>

<script>
import { ImagePreview } from 'vant';
import {
    buildVideoCoverUrl,
    createReactionState,
    createBabyAlbumEntry,
    deleteBabyAlbumEntry,
    fetchBabyAlbumGroups,
    resolveBabyAlbumToken,
    uploadBabyAlbumFile,
    updateBabyAlbumEntry
} from '@/api/babyAlbum';
import BabyAlbumCard from '@/components/baby/BabyAlbumCard.vue';
import {
    albumTodayGroup,
    createAlbumGroups,
    creatorOptions,
    mediaFilterOptions
} from './mock';

const ALBUM_SNAPSHOT_KEY_PREFIX = 'baby_album_groups_snapshot_v1';
const IMAGE_FILE_EXTENSIONS = new Set(['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'heic', 'heif', 'tif', 'tiff']);
const VIDEO_FILE_EXTENSIONS = new Set(['mp4', 'mov', 'm4v', 'avi', 'webm', '3gp', 'mkv']);
const MIME_TYPE_BY_EXTENSION = {
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    gif: 'image/gif',
    webp: 'image/webp',
    bmp: 'image/bmp',
    heic: 'image/heic',
    heif: 'image/heif',
    tif: 'image/tiff',
    tiff: 'image/tiff',
    mp4: 'video/mp4',
    mov: 'video/quicktime',
    m4v: 'video/x-m4v',
    avi: 'video/x-msvideo',
    webm: 'video/webm',
    '3gp': 'video/3gpp',
    mkv: 'video/x-matroska'
};
const ALBUM_PAGE_CLASS = 'baby-album-scroll-page';

function isQuickTimeCompatibilityUrl (url) {
    return /\.quicktime(?:[?#]|$)/i.test(String(url || ''));
}

function formatDateId (date = new Date()) {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');

    return `${year}-${month}-${day}`;
}

function formatDisplayDate (date = new Date()) {
    return `${date.getMonth() + 1}月${date.getDate()}日`;
}

function createLocalAssetId () {
    return `asset-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function createUploadJobId () {
    return `upload-job-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function resolveFileExtension (file) {
    const normalizedName = String(file?.name || '');
    const extensionMatch = normalizedName.match(/\.([^.]+)$/);
    return extensionMatch ? extensionMatch[1].toLowerCase() : '';
}

function resolveMediaKind (file) {
    const majorType = String(file?.type || '').split('/')[0];
    if (majorType === 'image' || majorType === 'video') {
        return majorType;
    }

    const extension = resolveFileExtension(file);
    if (IMAGE_FILE_EXTENSIONS.has(extension)) {
        return 'image';
    }
    if (VIDEO_FILE_EXTENSIONS.has(extension)) {
        return 'video';
    }

    return '';
}

function inferMimeTypeFromFile (file) {
    if (typeof file?.type === 'string' && file.type) {
        return file.type;
    }

    return MIME_TYPE_BY_EXTENSION[resolveFileExtension(file)] || '';
}

function normalizeMediaFile (file) {
    const inferredType = inferMimeTypeFromFile(file);
    if (!file || !inferredType || file.type === inferredType || typeof File !== 'function') {
        return file;
    }

    try {
        return new File([file], file.name || `bc-album-upload.${resolveFileExtension(file) || 'tmp'}`, {
            type: inferredType,
            lastModified: file.lastModified || Date.now()
        });
    } catch (error) {
        return file;
    }
}

function dataUrlToFile (dataUrl, fileName) {
    const matched = String(dataUrl || '').match(/^data:([^;,]+)(;base64)?,(.*)$/);
    if (!matched || typeof File !== 'function') {
        return null;
    }

    const mimeType = matched[1] || 'image/jpeg';
    if (!mimeType.startsWith('image/') || mimeType === 'image/svg+xml') {
        return null;
    }

    try {
        const payload = matched[3] || '';
        const binaryString = matched[2] ? atob(payload) : decodeURIComponent(payload);
        const bytes = new Uint8Array(binaryString.length);
        for (let index = 0; index < binaryString.length; index += 1) {
            bytes[index] = binaryString.charCodeAt(index);
        }
        return new File([bytes], fileName || 'video-thumbnail.jpg', {
            type: mimeType,
            lastModified: Date.now()
        });
    } catch (error) {
        return null;
    }
}

function buildAlbumSnapshotBucket (token = '') {
    const normalizedToken = String(token || 'anonymous');
    let hash = 0;

    for (let index = 0; index < normalizedToken.length; index += 1) {
        hash = ((hash << 5) - hash) + normalizedToken.charCodeAt(index);
        hash |= 0;
    }

    return `u${Math.abs(hash).toString(36)}`;
}

function createSmartUploadSummary () {
    return {
        sourceCount: 0,
        dedupedCount: 0,
        recommendedCount: 0,
        reviewCount: 0
    };
}

function buildComposerDraft (type) {
    return {
        mediaType: type === 'camera' ? 'video' : 'photo',
        caption: '',
        location: '客厅'
    };
}

function formatDurationText (seconds) {
    const safeSeconds = Math.max(0, Math.round(Number(seconds) || 0));
    const minutes = `${Math.floor(safeSeconds / 60)}`.padStart(2, '0');
    const remainSeconds = `${safeSeconds % 60}`.padStart(2, '0');

    return `${minutes}:${remainSeconds}`;
}

function buildVideoPlaceholderThumbnail (durationText = '00:00') {
    const safeDuration = durationText || '00:00';
    const svg = `
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 360 640">
            <defs>
                <linearGradient id="bg" x1="0%" x2="100%" y1="0%" y2="100%">
                    <stop offset="0%" stop-color="#283445"/>
                    <stop offset="100%" stop-color="#55657b"/>
                </linearGradient>
            </defs>
            <rect width="360" height="640" rx="42" fill="url(#bg)"/>
            <circle cx="180" cy="288" r="74" fill="rgba(255,255,255,0.14)"/>
            <polygon points="160,246 160,330 226,288" fill="#ffffff"/>
            <rect x="24" y="556" width="312" height="48" rx="24" fill="rgba(12,19,29,0.34)"/>
            <text x="44" y="587" fill="#ffffff" font-size="24" font-family="-apple-system,BlinkMacSystemFont,'PingFang SC',sans-serif">视频</text>
            <text x="316" y="587" text-anchor="end" fill="rgba(255,255,255,0.88)" font-size="22" font-family="-apple-system,BlinkMacSystemFont,'PingFang SC',sans-serif">${safeDuration}</text>
        </svg>
    `.trim();

    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}

function formatCandidateDateTime (timestamp) {
    const date = new Date(timestamp);
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    const hours = `${date.getHours()}`.padStart(2, '0');
    const minutes = `${date.getMinutes()}`.padStart(2, '0');

    return `${month}-${day} ${hours}:${minutes}`;
}

function resolveFileTakenAt (file) {
    const fileTimestamp = Number(file?.lastModified || 0);

    return fileTimestamp > 0 ? Math.floor(fileTimestamp / 1000) : Math.floor(Date.now() / 1000);
}

function formatDateTimeLabel (timestamp) {
    const date = new Date(Number(timestamp || 0) * 1000);
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    const hours = `${date.getHours()}`.padStart(2, '0');
    const minutes = `${date.getMinutes()}`.padStart(2, '0');

    return `${year}-${month}-${day} ${hours}:${minutes}`;
}

function resolveDateIdFromTimestamp (timestamp) {
    return formatDateId(new Date(Number(timestamp || 0) * 1000));
}

function createDateFromTimestamp (timestamp) {
    const safeTimestamp = Number(timestamp || 0);
    return new Date((safeTimestamp > 0 ? safeTimestamp : Math.floor(Date.now() / 1000)) * 1000);
}

function moveArrayItem (list, fromIndex, toIndex) {
    const nextList = list.slice();
    const [item] = nextList.splice(fromIndex, 1);
    nextList.splice(toIndex, 0, item);
    return nextList;
}

function areStringArraysEqual (first = [], second = []) {
    if (first.length !== second.length) {
        return false;
    }

    return first.every((item, index) => item === second[index]);
}

function buildLocalRelativeTime (timestamp) {
    const safeTimestamp = Number(timestamp || 0);
    const diffSeconds = Math.max(0, Math.floor(Date.now() / 1000) - safeTimestamp);

    if (diffSeconds < 60) {
        return '刚刚';
    }
    if (diffSeconds < 3600) {
        return `${Math.floor(diffSeconds / 60)}分钟前`;
    }
    if (diffSeconds < 86400) {
        return `${Math.floor(diffSeconds / 3600)}小时前`;
    }
    if (diffSeconds < 172800) {
        return '昨天';
    }
    return formatDisplayDate(new Date(safeTimestamp * 1000));
}

function bufferToHex (buffer) {
    return Array.from(new Uint8Array(buffer))
        .map(item => item.toString(16).padStart(2, '0'))
        .join('');
}

function resolveNativeAlbumNavState (route) {
    if (route?.query?.native_nav === '1') {
        return true;
    }

    if (typeof window === 'undefined') {
        return false;
    }

    return window.__BC_USE_NATIVE_ALBUM_NAV__ === true || window.__BC_USE_NATIVE_ALBUM_NAV__ === 'true';
}

function detectWechatBrowser () {
    if (typeof navigator === 'undefined') {
        return false;
    }

    return /micromessenger/i.test(navigator.userAgent || '');
}

function createUploadProgress (totalAssets = 0) {
    const safeTotalAssets = Math.max(0, Number(totalAssets || 0));

    return {
        totalAssets: safeTotalAssets,
        completedAssets: 0,
        uploadedAssets: 0,
        pendingAssets: safeTotalAssets,
        progressPercent: 0
    };
}

function clonePersistedEntry (entry = {}) {
    return {
        ...entry,
        images: Array.isArray(entry.images) ? entry.images.slice() : [],
        reactions: {
            ...createReactionState(),
            ...(entry.reactions || {})
        }
    };
}

function resolveAlbumPageMode (path = '') {
    if (path === '/baby/album/search') {
        return 'search';
    }
    if (path === '/baby/album/search/date') {
        return 'date';
    }
    if (path === '/baby/album/search/photo') {
        return 'photo';
    }
    if (path === '/baby/album/search/video') {
        return 'video';
    }
    return 'timeline';
}

function parseDateId (dateId) {
    const matched = String(dateId || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!matched) {
        return null;
    }

    const [, year, month, day] = matched;
    return new Date(Number(year), Number(month) - 1, Number(day));
}

function formatAbsoluteDateLabel (dateId) {
    const resolvedDate = parseDateId(dateId);
    if (!resolvedDate) {
        return '全部时间';
    }

    return `${resolvedDate.getFullYear()}年${resolvedDate.getMonth() + 1}月${resolvedDate.getDate()}日`;
}

function diffDateIds (leftDateId, rightDateId) {
    const leftDate = parseDateId(leftDateId);
    const rightDate = parseDateId(rightDateId);

    if (!leftDate || !rightDate) {
        return Number.POSITIVE_INFINITY;
    }

    return Math.abs(Math.round((leftDate.getTime() - rightDate.getTime()) / 86400000));
}

function formatRangeLabel (startDateId, endDateId) {
    const startDate = parseDateId(startDateId);
    const endDate = parseDateId(endDateId);

    if (!startDate || !endDate) {
        return '按日期分组';
    }

    if (startDateId === endDateId) {
        return formatAbsoluteDateLabel(startDateId);
    }

    const startYear = startDate.getFullYear();
    const endYear = endDate.getFullYear();
    const startMonth = startDate.getMonth() + 1;
    const endMonth = endDate.getMonth() + 1;
    const startDay = startDate.getDate();
    const endDay = endDate.getDate();

    if (startYear === endYear && startMonth === endMonth) {
        return `${startYear}年${startMonth}月${startDay}日至${endDay}日`;
    }

    if (startYear === endYear) {
        return `${startYear}年${startMonth}月${startDay}日至${endMonth}月${endDay}日`;
    }

    return `${startYear}年${startMonth}月${startDay}日至${endYear}年${endMonth}月${endDay}日`;
}

function addMonths (date, offset) {
    return new Date(date.getFullYear(), date.getMonth() + offset, 1);
}

function formatMonthLabel (date) {
    return `${date.getFullYear()}年 ${`${date.getMonth() + 1}`.padStart(2, '0')}月`;
}

function matchesAlbumKeyword (group, entry, normalizedKeyword) {
    if (!normalizedKeyword) {
        return true;
    }

    const haystack = [
        group.label,
        group.ageText,
        group.displayDate,
        entry.author,
        entry.caption,
        entry.location,
        formatAbsoluteDateLabel(group.id)
    ].join(' ').toLowerCase();

    return haystack.includes(normalizedKeyword);
}

function buildMediaRecords (groups = [], mediaType = 'photo', keyword = '') {
    const normalizedKeyword = String(keyword || '').trim().toLowerCase();
    const records = [];

    groups.forEach(group => {
        group.entries.forEach(entry => {
            if (entry.postType !== mediaType || !matchesAlbumKeyword(group, entry, normalizedKeyword)) {
                return;
            }

            if (mediaType === 'video') {
                const videoUrl = entry.videoUrl || entry.images[0] || '';
                const durationText = entry.videoDuration || '00:00';
                records.push({
                    id: `${entry.id}-video`,
                    entryId: String(entry.id),
                    mediaIndex: 0,
                    dateId: group.id,
                    postType: 'video',
                    caption: entry.caption || '',
                    location: entry.location || '',
                    thumbnail: entry.thumbnail || buildVideoCoverUrl(videoUrl) || entry.images[0] || buildVideoPlaceholderThumbnail(durationText),
                    videoDuration: durationText,
                    videoUrl,
                    selectable: entry.uploadStatus === 'done',
                    takenAt: Number(entry.takenAt || 0),
                    entry
                });
                return;
            }

            (Array.isArray(entry.images) ? entry.images : [])
                .filter(Boolean)
                .forEach((image, imageIndex) => {
                    records.push({
                        id: `${entry.id}-photo-${imageIndex}`,
                        entryId: String(entry.id),
                        mediaIndex: imageIndex,
                        dateId: group.id,
                        postType: 'photo',
                        caption: entry.caption || '',
                        location: entry.location || '',
                        thumbnail: image,
                        videoDuration: '',
                        selectable: entry.uploadStatus === 'done',
                        takenAt: Number(entry.takenAt || 0),
                        entry
                    });
                });
        });
    });

    return records.sort((left, right) => Number(right.takenAt || 0) - Number(left.takenAt || 0));
}

function buildMediaSections (records = []) {
    const order = [];
    const bucketMap = new Map();

    records.forEach(record => {
        if (!bucketMap.has(record.dateId)) {
            bucketMap.set(record.dateId, []);
            order.push(record.dateId);
        }

        bucketMap.get(record.dateId).push(record);
    });

    const sections = [];
    order.forEach(dateId => {
        const items = bucketMap.get(dateId) || [];
        const currentSection = sections[sections.length - 1];

        if (!currentSection || diffDateIds(currentSection.oldestDateId, dateId) > 1) {
            sections.push({
                id: `${dateId}-${dateId}`,
                newestDateId: dateId,
                oldestDateId: dateId,
                items: items.slice()
            });
            return;
        }

        currentSection.oldestDateId = dateId;
        currentSection.id = `${currentSection.newestDateId}-${currentSection.oldestDateId}`;
        currentSection.items.push(...items);
    });

    return sections.map(section => ({
        id: section.id,
        label: formatRangeLabel(section.oldestDateId, section.newestDateId),
        items: section.items
    }));
}

function buildCalendarMonths (activeDateSet, selectedDateId, todayDateId) {
    const seedDateIds = [
        ...Array.from(activeDateSet || new Set()),
        selectedDateId && selectedDateId !== 'all' ? selectedDateId : '',
        todayDateId || ''
    ].filter(Boolean);

    const parsedDates = seedDateIds
        .map(parseDateId)
        .filter(Boolean)
        .sort((left, right) => left.getTime() - right.getTime());

    const baseDate = parsedDates[0] || new Date();
    const startMonth = new Date(baseDate.getFullYear(), baseDate.getMonth(), 1);
    const endMonthSeed = parsedDates[parsedDates.length - 1] || new Date();
    const endMonth = new Date(endMonthSeed.getFullYear(), endMonthSeed.getMonth(), 1);
    const months = [];

    for (let cursor = startMonth; cursor.getTime() <= endMonth.getTime(); cursor = addMonths(cursor, 1)) {
        const monthStart = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
        const monthEnd = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0);
        const leadingBlankCount = monthStart.getDay();
        const totalCells = leadingBlankCount + monthEnd.getDate();
        const trailingBlankCount = (7 - (totalCells % 7)) % 7;
        const days = [];

        for (let index = 0; index < leadingBlankCount; index += 1) {
            days.push({
                key: `${monthStart.getFullYear()}-${monthStart.getMonth() + 1}-leading-${index}`,
                blank: true
            });
        }

        for (let dayNumber = 1; dayNumber <= monthEnd.getDate(); dayNumber += 1) {
            const cellDate = new Date(monthStart.getFullYear(), monthStart.getMonth(), dayNumber);
            const cellDateId = formatDateId(cellDate);
            days.push({
                key: cellDateId,
                blank: false,
                id: cellDateId,
                day: dayNumber,
                hasEntries: activeDateSet.has(cellDateId),
                isToday: cellDateId === todayDateId,
                isSelected: selectedDateId === cellDateId
            });
        }

        for (let index = 0; index < trailingBlankCount; index += 1) {
            days.push({
                key: `${monthStart.getFullYear()}-${monthStart.getMonth() + 1}-trailing-${index}`,
                blank: true
            });
        }

        months.push({
            id: `${monthStart.getFullYear()}-${monthStart.getMonth() + 1}`,
            label: formatMonthLabel(monthStart),
            days
        });
    }

    return months;
}

export default {
    name: 'BabyAlbumGallery',
    components: {
        BabyAlbumCard
    },
    data () {
        return {
            creatorItems: creatorOptions,
            mediaFilters: mediaFilterOptions,
            groups: [],
            smartCandidates: [],
            smartUploadSummary: createSmartUploadSummary(),
            token: '',
            keyword: '',
            currentDate: 'all',
            activeMenuId: '',
            showDateSheet: false,
            showSearch: false,
            isLoadingAlbum: true,
            hasResolvedInitialLoad: false,
            loadError: '',
            activeFilter: 'all',
            useNativeAlbumNav: false,
            isWechatBrowser: false,
            nativeAlbumBridge: null,
            stickyShellHeight: 120,
            stickyResizeObserver: null,
            observedStickyShell: null,
            showCreatePanel: false,
            activeComposer: '',
            composerForm: buildComposerDraft('photo'),
            composerAssets: [],
            uploadJobs: [],
            editDraft: {
                caption: '',
                location: '',
                takenAt: Math.floor(Date.now() / 1000),
                images: [],
                thumbnail: ''
            },
            isSubmittingComposer: false,
            editingEntryId: '',
            showEditTimePicker: false,
            editPickerDate: new Date(),
            editMinDate: new Date(Date.now() - (10 * 365 * 86400000)),
            editMaxDate: new Date(),
            todayGroupId: formatDateId(),
            todayDisplayDate: formatDisplayDate(),
            searchHubKeyword: '',
            showMediaActionSheet: false,
            batchDeleteMode: false,
            batchDeletePending: false,
            selectedMediaIds: [],
            loadedMediaThumbnailMap: {},
            videoPreviewEntry: null,
            videoPreviewBlobUrl: '',
            videoPreviewBlobSourceUrl: '',
            videoPreviewBlobLoading: false,
            showVideoPreviewPlayButton: false,
            isHomeChromeCollapsed: false,
            lastHomeScrollY: 0,
            homeScrollRaf: 0,
            homeScrollContainer: null,
            albumTouchStartY: 0,
            albumTouchLastY: 0,
            weekdays: ['日', '一', '二', '三', '四', '五', '六']
        };
    },
    computed: {
        pageMode () {
            return resolveAlbumPageMode(this.$route.path);
        },
        currentBabyId () {
            return this.$route.query.baby_id || this.$route.query.babyId || '';
        },
        isAlbumRootPage () {
            return this.pageMode === 'timeline';
        },
        isSearchHubPage () {
            return this.pageMode === 'search';
        },
        isDateExplorerPage () {
            return this.pageMode === 'date';
        },
        routeDateId () {
            const routeDate = typeof this.$route.query.date === 'string'
                ? this.$route.query.date
                : '';

            return parseDateId(routeDate) ? routeDate : '';
        },
        isDateSearchResultRoute () {
            return this.isDateExplorerPage || this.$route.query.album_search === 'date';
        },
        selectedSearchDateId () {
            return this.isDateSearchResultRoute ? this.routeDateId : '';
        },
        isDateResultGridPage () {
            return Boolean(this.selectedSearchDateId);
        },
        isPhotoGridPage () {
            return this.pageMode === 'photo';
        },
        isVideoGridPage () {
            return this.pageMode === 'video';
        },
        isSearchSubpage () {
            return this.isSearchHubPage || this.isDateExplorerPage || this.isMediaGridPage;
        },
        isMediaGridPage () {
            return this.isPhotoGridPage || this.isVideoGridPage;
        },
        isSearchResultGridPage () {
            return this.isMediaGridPage || this.isDateResultGridPage;
        },
        shouldUseNativeRootNav () {
            return this.useNativeAlbumNav && this.isAlbumRootPage;
        },
        shouldRenderHomeStickyShell () {
            return this.isAlbumRootPage && !(this.shouldUseNativeRootNav && this.isHomeChromeCollapsed);
        },
        pageTitle () {
            if (this.isDateResultGridPage) {
                return this.selectedDateLabel;
            }
            if (this.isDateExplorerPage) {
                return '按日期查找';
            }
            if (this.isPhotoGridPage) {
                return '照片';
            }
            if (this.isVideoGridPage) {
                return '视频';
            }
            return '宝宝相册';
        },
        todayGroup () {
            const matchedGroup = this.groups.find(group => group.id === this.todayGroupId);

            if (matchedGroup) {
                return matchedGroup;
            }

            const fallbackAgeText = this.groups.find(group => group.ageText)?.ageText || albumTodayGroup.ageText;

            return {
                id: this.todayGroupId,
                label: '今天',
                displayDate: this.todayDisplayDate,
                ageText: fallbackAgeText,
                entries: []
            };
        },
        dateOptions () {
            const options = [
                {
                    value: 'all',
                    label: '全部时间'
                },
                {
                    value: this.todayGroup.id,
                    label: this.todayGroup.displayDate
                }
            ];
            const existingValues = new Set(options.map(item => item.value));

            this.groups.forEach(group => {
                if (existingValues.has(group.id)) {
                    return;
                }

                existingValues.add(group.id);
                options.push({
                    value: group.id,
                    label: group.displayDate || group.label
                });
            });

            return options;
        },
        currentDateLabel () {
            const matchedDate = this.dateOptions.find(item => item.value === this.currentDate);
            return matchedDate ? matchedDate.label : '全部时间';
        },
        selectedDateLabel () {
            if (this.isDateResultGridPage) {
                return formatAbsoluteDateLabel(this.selectedSearchDateId);
            }

            return this.currentDate === 'all' ? '全部时间' : formatAbsoluteDateLabel(this.currentDate);
        },
        filteredGroups () {
            const baseGroups = this.currentDate === 'all'
                ? this.groups
                : this.groups.filter(group => group.id === this.currentDate);

            return baseGroups.filter(group => Array.isArray(group.entries) && group.entries.length);
        },
        isPrototypeMode () {
            return !this.token;
        },
        shouldShowInitialLoading () {
            return !this.hasResolvedInitialLoad && this.isLoadingAlbum;
        },
        pageMediaFilter () {
            if (this.isPhotoGridPage) {
                return 'photo';
            }
            if (this.isVideoGridPage) {
                return 'video';
            }
            return 'all';
        },
        activeUploadSummary () {
            const relevantJobs = this.uploadJobs.filter(job => {
                if (job.status === 'failed' || job.status === 'done') {
                    return false;
                }

                return this.pageMediaFilter === 'all' || job.postType === this.pageMediaFilter;
            });

            if (!relevantJobs.length) {
                return null;
            }

            const totalAssets = relevantJobs.reduce((sum, job) => sum + Number(job.totalAssets || 0), 0);
            const completedAssets = relevantJobs.reduce((sum, job) => sum + Number(job.completedAssets || 0), 0);
            const uploadedWeight = relevantJobs.reduce((sum, job) => (
                sum + (Array.isArray(job.assetProgresses)
                    ? job.assetProgresses.reduce((assetSum, progress) => assetSum + (Math.max(0, Math.min(100, Number(progress || 0))) / 100), 0)
                    : 0)
            ), 0);

            return {
                totalAssets,
                completedAssets,
                pendingAssets: Math.max(0, totalAssets - completedAssets),
                progressPercent: totalAssets ? Math.max(1, Math.min(99, Math.round((uploadedWeight / totalAssets) * 100))) : 0
            };
        },
        uploadProgressTitle () {
            if (!this.activeUploadSummary) {
                return '';
            }

            if (this.pageMediaFilter === 'photo') {
                return '照片上传中';
            }
            if (this.pageMediaFilter === 'video') {
                return '视频上传中';
            }
            return '照片或视频上传中';
        },
        uploadProgressCountLabel () {
            if (!this.activeUploadSummary) {
                return '';
            }

            return `${this.activeUploadSummary.completedAssets}/${this.activeUploadSummary.totalAssets}`;
        },
        photoMediaRecords () {
            return buildMediaRecords(this.groups, 'photo', this.searchHubKeyword);
        },
        videoMediaRecords () {
            return buildMediaRecords(this.groups, 'video', this.searchHubKeyword);
        },
        dateMediaRecords () {
            if (!this.isDateResultGridPage) {
                return [];
            }

            return [
                ...this.photoMediaRecords,
                ...this.videoMediaRecords
            ]
                .filter(item => item.dateId === this.selectedSearchDateId)
                .sort((left, right) => Number(right.takenAt || 0) - Number(left.takenAt || 0));
        },
        currentMediaSections () {
            if (this.isDateResultGridPage) {
                return buildMediaSections(this.dateMediaRecords);
            }

            return buildMediaSections(this.isPhotoGridPage ? this.photoMediaRecords : this.videoMediaRecords);
        },
        currentMediaItems () {
            return this.currentMediaSections.reduce((result, section) => result.concat(section.items), []);
        },
        mediaEmptyTitle () {
            if (this.isDateResultGridPage) {
                return '这一天没有匹配的照片或视频';
            }
            if (this.searchHubKeyword.trim()) {
                return this.isPhotoGridPage ? '没有找到匹配的照片' : '没有找到匹配的视频';
            }

            return this.isPhotoGridPage ? '还没有照片' : '还没有视频';
        },
        mediaEmptyDescription () {
            if (this.isDateResultGridPage) {
                return this.searchHubKeyword.trim()
                    ? '换一个关键词试试，或者重新选择其它有内容的日期。'
                    : '重新选择其它有内容的日期，继续查看宝宝成长片段。';
            }
            if (this.searchHubKeyword.trim()) {
                return '换一个关键词试试，或者回到搜索入口查看其它分类。';
            }

            return this.isPhotoGridPage ? '去首页发布照片后，这里会按日期区间自动整理成网格。' : '去首页发布视频后，这里会按日期区间自动整理成网格。';
        },
        mediaEmptyActionText () {
            return this.isDateResultGridPage ? '重新选择日期' : '回到搜索入口';
        },
        activeDateIdSet () {
            return new Set(this.groups.filter(group => Array.isArray(group.entries) && group.entries.length).map(group => group.id));
        },
        calendarMonths () {
            const selectedDateId = this.selectedSearchDateId || (this.currentDate === 'all' ? this.todayGroupId : this.currentDate);
            return buildCalendarMonths(this.activeDateIdSet, selectedDateId, this.todayGroupId);
        },
        albumDayCount () {
            return this.activeDateIdSet.size;
        },
        albumPhotoCount () {
            return buildMediaRecords(this.groups, 'photo').length;
        },
        albumVideoCount () {
            return buildMediaRecords(this.groups, 'video').length;
        },
        searchHubCards () {
            const matchedDateIds = new Set([
                ...this.photoMediaRecords.map(item => item.dateId),
                ...this.videoMediaRecords.map(item => item.dateId)
            ]);

            return [
                {
                    key: 'date',
                    label: '日期',
                    icon: '日',
                    metric: `${matchedDateIds.size} 天`,
                    note: '按日期查看有内容的时间'
                },
                {
                    key: 'photo',
                    label: '照片',
                    icon: '照',
                    metric: `${this.photoMediaRecords.length} 张`,
                    note: this.searchHubKeyword.trim() ? '按关键词筛到的照片结果' : '进入照片网格页查看'
                },
                {
                    key: 'video',
                    label: '视频',
                    icon: '影',
                    metric: `${this.videoMediaRecords.length} 条`,
                    note: this.searchHubKeyword.trim() ? '按关键词筛到的视频结果' : '进入视频网格页查看'
                }
            ];
        },
        selectedMediaCount () {
            return this.selectedMediaIds.length;
        },
        currentMediaTypeLabel () {
            if (this.isDateResultGridPage) {
                return '照片或视频';
            }
            return this.isVideoGridPage ? '视频' : '照片';
        },
        composerTitle () {
            const titleMap = {
                photo: '发布照片或视频',
                camera: '快速拍摄',
                'smart-upload': '智能上传候选',
                edit: '编辑动态'
            };

            return titleMap[this.activeComposer] || '记录成长';
        },
        composerSubtitle () {
            const subtitleMap = {
                photo: '选择真实图片或视频文件，提交后会先上传媒体再写入相册记录。',
                camera: '当前优先调用系统拍摄/录像文件选择，成功后直接写入宝宝相册。',
                'smart-upload': '候选素材需人工勾选确认，导入时会转成真实相册记录。',
                edit: '当前支持修改文案、地点、记录时间和照片顺序。'
            };

            return subtitleMap[this.activeComposer] || '';
        },
        selectedCandidateCount () {
            return this.smartCandidates.filter(item => item.selected).length;
        },
        recommendedSmartCandidateCount () {
            return this.smartCandidates.filter(item => item.qualityLabel === '推荐导入').length;
        },
        reviewSmartCandidateCount () {
            return this.smartCandidates.filter(item => item.qualityLabel !== '推荐导入').length;
        },
        smartCandidateSummaryText () {
            if (!this.smartCandidates.length) {
                return '';
            }

            const summary = this.smartUploadSummary;
            return `已分析 ${summary.sourceCount} 条系统相册素材，自动去重 ${summary.dedupedCount} 条，默认推荐 ${summary.recommendedCount} 条，剩余 ${summary.reviewCount} 条待人工确认。`;
        },
        composerAllowsMultiple () {
            return this.activeComposer === 'smart-upload'
                || this.activeComposer === 'photo'
                || this.activeComposer === 'camera'
                || this.composerForm.mediaType === 'photo';
        },
        composerInputAccept () {
            if (this.activeComposer === 'smart-upload') {
                return 'image/*,video/*';
            }

            if (this.activeComposer === 'photo') {
                return 'image/*,video/*';
            }

            return this.composerForm.mediaType === 'video' ? 'video/*' : 'image/*';
        },
        composerPickerLabel () {
            if (this.activeComposer === 'photo') {
                return '选择图片或视频文件';
            }
            if (this.activeComposer === 'camera') {
                return this.composerForm.mediaType === 'video' ? '开始录像或选择视频' : '打开相机拍照';
            }

            return this.composerForm.mediaType === 'video' ? '选择视频文件' : '选择图片文件';
        },
        composerHasAssets () {
            return this.composerAssets.length > 0;
        },
        canSubmitComposer () {
            if (this.activeComposer === 'photo' || this.activeComposer === 'camera') {
                return this.composerHasAssets && !this.isSubmittingComposer;
            }

            if (this.activeComposer === 'smart-upload') {
                return this.selectedCandidateCount > 0 && !this.isSubmittingComposer;
            }

            return !this.isSubmittingComposer;
        },
        editingEntry () {
            return this.findEntryById(this.editingEntryId);
        },
        formattedEditTakenAt () {
            return formatDateTimeLabel(this.editDraft.takenAt);
        },
        editMediaItems () {
            if (!this.editingEntry) {
                return [];
            }

            if (this.editingEntry.postType === 'video') {
                return [{
                    id: `${this.editingEntry.id}-video`,
                    previewUrl: this.editDraft.thumbnail || this.editingEntry.thumbnail || this.editingEntry.images[0] || '',
                    alt: this.editingEntry.caption || 'video-cover',
                    badge: this.editingEntry.videoDuration || ''
                }];
            }

            return this.editDraft.images.map((image, index) => ({
                id: `${this.editingEntry.id}-${index}-${image}`,
                previewUrl: image,
                alt: `${this.editingEntry.caption || 'album-image'}-${index + 1}`,
                badge: ''
            }));
        },
        videoPreviewPoster () {
            if (!this.videoPreviewEntry) {
                return '';
            }

            const durationText = this.videoPreviewEntry.videoDuration || '00:00';
            return this.videoPreviewEntry.thumbnail
                || this.videoPreviewEntry.images?.[0]
                || buildVideoCoverUrl(this.videoPreviewEntry.videoUrl)
                || buildVideoPlaceholderThumbnail(durationText);
        },
        activeVideoPreviewUrl () {
            if (!this.videoPreviewEntry?.videoUrl) {
                return '';
            }
            if (isQuickTimeCompatibilityUrl(this.videoPreviewEntry.videoUrl) && !this.videoPreviewBlobUrl) {
                return '';
            }
            return this.videoPreviewBlobUrl || this.videoPreviewEntry.videoUrl;
        }
    },
    mounted () {
        this.installAlbumPageChrome();
        this.syncNativeAlbumNavState();
        this.isWechatBrowser = detectWechatBrowser();
        this.installNativeAlbumBridge();
        this.installStickyShellObserver();
        this.installHomeScrollObserver();
        this.syncRouteDrivenState();
        this.initializeAlbum();
    },
    beforeDestroy () {
        this.removeAlbumPageChrome();
        this.removeNativeAlbumBridge();
        this.removeHomeScrollObserver();
        if (this.stickyResizeObserver) {
            this.stickyResizeObserver.disconnect();
            this.stickyResizeObserver = null;
        }
        this.observedStickyShell = null;
        this.releaseComposerAssets(this.composerAssets);
        this.releaseSmartCandidates(this.smartCandidates);
        this.collectLocalEntries()
            .filter(entry => entry.persisted === false)
            .forEach(entry => {
                this.releaseEntryObjectUrls(entry);
            });
    },
    watch: {
        '$route': {
            immediate: true,
            handler () {
                this.syncRouteDrivenState();
            }
        },
        '$route.query.compose': {
            immediate: true,
            handler (compose) {
                if (!compose) {
                    return;
                }

                this.openComposerByQuery(compose);
            }
        },
        '$route.query.native_nav' () {
            this.syncNativeAlbumNavState();
        },
        activeComposer () {
            this.syncStickyShellHeight();
        },
        activeUploadSummary () {
            this.syncStickyShellHeight();
        },
        isHomeChromeCollapsed () {
            this.syncStickyShellHeight();
            this.notifyNativeNavigationState();
        },
        shouldRenderHomeStickyShell () {
            this.$nextTick(this.observeStickyShell);
            this.syncStickyShellHeight();
        }
    },
    methods: {
        installAlbumPageChrome () {
            if (typeof document === 'undefined') {
                return;
            }

            document.documentElement.classList.add(ALBUM_PAGE_CLASS);
            document.body?.classList.add(ALBUM_PAGE_CLASS);
            const appRoot = document.getElementById('app');
            if (appRoot) {
                appRoot.classList.add(ALBUM_PAGE_CLASS);
            }
        },
        removeAlbumPageChrome () {
            if (typeof document === 'undefined') {
                return;
            }

            document.documentElement.classList.remove(ALBUM_PAGE_CLASS);
            document.body?.classList.remove(ALBUM_PAGE_CLASS);
            const appRoot = document.getElementById('app');
            if (appRoot) {
                appRoot.classList.remove(ALBUM_PAGE_CLASS);
            }
        },
        albumSnapshotStorageKey () {
            return `${ALBUM_SNAPSHOT_KEY_PREFIX}:${buildAlbumSnapshotBucket(this.token)}`;
        },
        syncRouteDrivenState () {
            const routeDate = typeof this.$route.query.date === 'string'
                ? this.$route.query.date
                : 'all';
            this.currentDate = routeDate || 'all';
            this.searchHubKeyword = typeof this.$route.query.keyword === 'string'
                ? this.$route.query.keyword
                : '';

            if (!this.isSearchResultGridPage) {
                this.batchDeleteMode = false;
                this.selectedMediaIds = [];
            }

            this.showMediaActionSheet = false;
            this.normalizeDateSearchResultRoute();
            if (!this.isAlbumRootPage) {
                this.isHomeChromeCollapsed = false;
            }
            this.syncNativeAlbumNavState();
            this.notifyNativeNavigationState();
            this.syncStickyShellHeight();
        },
        normalizeDateSearchResultRoute () {
            if (this.pageMode !== 'timeline' || this.$route.query.album_search !== 'date' || !this.routeDateId) {
                return;
            }

            this.$router.replace({
                path: '/baby/album/search/date',
                query: this.buildAlbumQuery({
                    date: this.routeDateId,
                    album_search: 'date'
                }, {
                    keepKeyword: true
                })
            }).catch(() => {});
        },
        syncNativeAlbumNavState () {
            this.useNativeAlbumNav = resolveNativeAlbumNavState(this.$route);
        },
        installStickyShellObserver () {
            if (typeof ResizeObserver === 'undefined') {
                this.$nextTick(() => {
                    this.syncStickyShellHeight();
                });
                return;
            }

            if (!this.stickyResizeObserver) {
                this.stickyResizeObserver = new ResizeObserver(() => {
                    this.syncStickyShellHeight();
                });
            }

            this.$nextTick(() => {
                this.observeStickyShell();
                this.syncStickyShellHeight();
            });
        },
        observeStickyShell () {
            if (!this.stickyResizeObserver) {
                return;
            }

            const stickyShell = this.$refs.stickyShell || null;
            if (stickyShell === this.observedStickyShell) {
                return;
            }
            if (this.observedStickyShell) {
                this.stickyResizeObserver.unobserve(this.observedStickyShell);
            }
            this.observedStickyShell = stickyShell;
            if (stickyShell) {
                this.stickyResizeObserver.observe(stickyShell);
            }
        },
        installHomeScrollObserver () {
            if (typeof window === 'undefined') {
                return;
            }

            this.lastHomeScrollY = this.getPageScrollTop();
            window.addEventListener('scroll', this.handleHomeScroll, {
                passive: true
            });
            const appRoot = document.getElementById('app');
            if (appRoot && appRoot !== window) {
                this.homeScrollContainer = appRoot;
                appRoot.addEventListener('scroll', this.handleHomeScroll, {
                    passive: true
                });
            }
        },
        removeHomeScrollObserver () {
            if (typeof window === 'undefined') {
                return;
            }

            window.removeEventListener('scroll', this.handleHomeScroll);
            if (this.homeScrollContainer) {
                this.homeScrollContainer.removeEventListener('scroll', this.handleHomeScroll);
                this.homeScrollContainer = null;
            }
            if (this.homeScrollRaf) {
                window.cancelAnimationFrame(this.homeScrollRaf);
                this.homeScrollRaf = 0;
            }
        },
        getPageScrollTop () {
            if (typeof document === 'undefined') {
                return 0;
            }

            const appRoot = document.getElementById('app');
            if (appRoot && appRoot.scrollHeight > appRoot.clientHeight) {
                return Math.max(0, Number(appRoot.scrollTop || 0));
            }
            const scrollingElement = document.scrollingElement || document.documentElement || document.body;
            return Math.max(0, Number(scrollingElement?.scrollTop || window.pageYOffset || 0));
        },
        handleHomeScroll () {
            if (this.homeScrollRaf || typeof window === 'undefined') {
                return;
            }

            this.homeScrollRaf = window.requestAnimationFrame(() => {
                this.homeScrollRaf = 0;
                this.updateHomeChromeByScroll();
            });
        },
        updateHomeChromeByScroll () {
            const nextScrollY = this.getPageScrollTop();

            if (!this.isAlbumRootPage) {
                this.lastHomeScrollY = nextScrollY;
                this.isHomeChromeCollapsed = false;
                return;
            }

            const deltaY = nextScrollY - this.lastHomeScrollY;
            this.lastHomeScrollY = nextScrollY;

            if (nextScrollY < 24) {
                this.isHomeChromeCollapsed = false;
                return;
            }

            if (deltaY > 8 && nextScrollY > 72) {
                this.isHomeChromeCollapsed = true;
                return;
            }

            if (deltaY < -8) {
                this.isHomeChromeCollapsed = false;
            }
        },
        syncStickyShellHeight () {
            this.$nextTick(() => {
                if (!this.isAlbumRootPage) {
                    this.stickyShellHeight = 0;
                    return;
                }

                if (!this.shouldRenderHomeStickyShell) {
                    this.stickyShellHeight = 0;
                    return;
                }

                const stickyShell = this.$refs.stickyShell;
                if (!stickyShell) {
                    this.stickyShellHeight = 0;
                    return;
                }

                const measuredHeight = Math.ceil(stickyShell.getBoundingClientRect().height || stickyShell.scrollHeight || 0);
                const maxHomeChromeHeight = this.shouldUseNativeRootNav ? 238 : 300;
                const nextHeight = Math.min(measuredHeight, maxHomeChromeHeight);
                if (nextHeight !== this.stickyShellHeight) {
                    this.stickyShellHeight = nextHeight;
                }
            });
        },
        buildAlbumQuery (extraQuery = {}, { keepKeyword = false } = {}) {
            const nextQuery = { ...this.$route.query };

            delete nextQuery.compose;
            delete nextQuery.date;
            delete nextQuery.keyword;
            delete nextQuery.album_search;

            if (keepKeyword && this.searchHubKeyword.trim()) {
                nextQuery.keyword = this.searchHubKeyword.trim();
            }

            return {
                ...nextQuery,
                ...extraQuery
            };
        },
        pushAlbumPath (path, options = {}) {
            const {
                extraQuery = {},
                keepKeyword = false,
                replace = false
            } = options;
            const method = replace ? 'replace' : 'push';

            this.$router[method]({
                path,
                query: this.buildAlbumQuery(extraQuery, {
                    keepKeyword
                })
            }).catch(() => {});
        },
        notifyNativeNavigationState () {
            if (typeof window === 'undefined' || !window.webkit || !window.webkit.messageHandlers) {
                return;
            }

            const handler = window.webkit.messageHandlers.bcAlbumNavigationState;
            if (!handler || typeof handler.postMessage !== 'function') {
                return;
            }

            handler.postMessage({
                mode: this.pageMode,
                title: this.pageTitle,
                showNavigationBar: this.shouldUseNativeRootNav,
                showActions: this.shouldUseNativeRootNav,
                showSearchAction: this.shouldUseNativeRootNav && this.isHomeChromeCollapsed,
                href: window.location.href || ''
            });
        },
        readAlbumSnapshot () {
            if (typeof window === 'undefined' || !window.sessionStorage) {
                return [];
            }

            try {
                const rawSnapshot = window.sessionStorage.getItem(this.albumSnapshotStorageKey());
                if (!rawSnapshot) {
                    return [];
                }

                const parsedSnapshot = JSON.parse(rawSnapshot);
                return Array.isArray(parsedSnapshot)
                    ? parsedSnapshot.map(group => ({
                        ...group,
                        entries: Array.isArray(group.entries)
                            ? group.entries.map(entry => clonePersistedEntry({
                                ...entry,
                                persisted: true
                            }))
                            : []
                    }))
                    : [];
            } catch (error) {
                return [];
            }
        },
        writeAlbumSnapshot () {
            if (typeof window === 'undefined' || !window.sessionStorage) {
                return;
            }

            try {
                const storageKey = this.albumSnapshotStorageKey();
                const snapshotGroups = this.groups
                    .map(group => ({
                        ...group,
                        entries: group.entries
                            .filter(entry => entry.persisted)
                            .map(entry => clonePersistedEntry(entry))
                    }))
                    .filter(group => group.entries.length);

                if (!snapshotGroups.length) {
                    window.sessionStorage.removeItem(storageKey);
                    return;
                }

                window.sessionStorage.setItem(storageKey, JSON.stringify(snapshotGroups));
            } catch (error) {
                // ignore snapshot persistence failures
            }
        },
        postAlbumDebug (payload = {}) {
            if (typeof window === 'undefined' || !window.webkit || !window.webkit.messageHandlers) {
                return;
            }

            const handler = window.webkit.messageHandlers.bcAlbumDebug;
            if (!handler || typeof handler.postMessage !== 'function') {
                return;
            }

            handler.postMessage({
                type: payload.type || 'album_event',
                href: window.location.href || '',
                title: document.title || '',
                readyState: document.readyState || '',
                payload
            });
        },
        handleAlbumTouchStart (event) {
            this.captureAlbumPointer(event);
            const touch = event?.touches?.[0];
            if (!touch) {
                return;
            }

            this.albumTouchStartY = touch.clientY;
            this.albumTouchLastY = touch.clientY;
        },
        handleAlbumTouchMove (event) {
            const touch = event?.touches?.[0];
            if (!touch || !this.isAlbumRootPage) {
                return;
            }

            const currentY = touch.clientY;
            const deltaFromLast = this.albumTouchLastY ? this.albumTouchLastY - currentY : 0;
            const deltaFromStart = this.albumTouchStartY ? this.albumTouchStartY - currentY : 0;
            this.albumTouchLastY = currentY;

            if (this.getPageScrollTop() < 24 && deltaFromStart < 0) {
                this.isHomeChromeCollapsed = false;
                return;
            }

            if (deltaFromLast > 4 && deltaFromStart > 12) {
                this.isHomeChromeCollapsed = true;
                this.lastHomeScrollY = this.getPageScrollTop();
                return;
            }

            if (deltaFromLast < -4 && deltaFromStart < -12) {
                this.isHomeChromeCollapsed = false;
                this.lastHomeScrollY = this.getPageScrollTop();
            }
        },
        handleAlbumTouchEnd () {
            this.albumTouchStartY = 0;
            this.albumTouchLastY = 0;
        },
        captureAlbumPointer (event) {
            const target = event?.target;
            if (!target || typeof target.closest !== 'function') {
                return;
            }

            const matched = target.closest('.more-button,.menu-button,.floating-menu,.overlay-mask,.batch-primary');
            if (!matched) {
                return;
            }

            this.postAlbumDebug({
                type: 'album_pointer_capture',
                eventType: event.type,
                targetClass: matched.className || '',
                activeMenuId: this.activeMenuId || '',
                showCreatePanel: this.showCreatePanel,
                activeComposer: this.activeComposer || '',
                showDateSheet: this.showDateSheet,
                showEditTimePicker: this.showEditTimePicker,
                showMediaActionSheet: this.showMediaActionSheet,
                batchDeleteMode: this.batchDeleteMode,
                clientY: event.touches && event.touches[0] ? event.touches[0].clientY : event.clientY
            });
        },
        hydrateAlbumSnapshot () {
            if (!this.token) {
                return;
            }

            const snapshotGroups = this.readAlbumSnapshot();
            if (!snapshotGroups.length) {
                return;
            }

            this.groups = snapshotGroups;
            this.hasResolvedInitialLoad = true;
            this.isLoadingAlbum = false;
        },
        installNativeAlbumBridge () {
            if (typeof window === 'undefined') {
                return;
            }

            this.syncNativeAlbumNavState();
            this.nativeAlbumBridge = {
                openSearchHub: () => {
                    this.openSearchHub();
                    return 'ok';
                },
                toggleSearch: () => {
                    this.openSearchHub();
                    return 'ok';
                },
                openCreatePanel: () => {
                    this.openCreatePanel();
                    return 'ok';
                }
            };
            window.bcNativeAlbum = this.nativeAlbumBridge;
            this.notifyNativeNavigationState();
        },
        removeNativeAlbumBridge () {
            if (typeof window === 'undefined') {
                return;
            }

            if (window.bcNativeAlbum === this.nativeAlbumBridge) {
                delete window.bcNativeAlbum;
            }
        },
        async initializeAlbum () {
            this.token = resolveBabyAlbumToken(this.$route);
            this.hydrateAlbumSnapshot();
            await this.loadAlbumGroups({
                preserveVisibleState: this.groups.length > 0
            });
            this.notifyNativeNavigationState();
        },
        async loadAlbumGroups ({ preserveVisibleState = false } = {}) {
            if (!this.token) {
                this.groups = createAlbumGroups();
                this.loadError = '';
                this.isLoadingAlbum = false;
                this.hasResolvedInitialLoad = true;
                this.syncStickyShellHeight();
                return;
            }

            if (!preserveVisibleState) {
                this.isLoadingAlbum = true;
            }
            this.loadError = '';

            try {
                this.groups = await fetchBabyAlbumGroups({
                    token: this.token,
                    babyId: this.currentBabyId
                });
                this.writeAlbumSnapshot();

            } catch (error) {
                if (!preserveVisibleState) {
                    this.groups = [];
                    this.loadError = error.message || '加载宝宝相册失败';
                } else {
                    this.$toast(error.message || '加载宝宝相册失败');
                }
            } finally {
                this.isLoadingAlbum = false;
                this.hasResolvedInitialLoad = true;
                this.syncStickyShellHeight();
            }
        },
        selectComposerMediaType (mediaType) {
            if (this.composerForm.mediaType === mediaType) {
                return;
            }

            this.composerForm.mediaType = mediaType;
            this.composerAssets = [];
        },
        notifyNativePickerMode (pickerMode) {
            if (typeof window === 'undefined' || !window.webkit || !window.webkit.messageHandlers) {
                return;
            }

            const handler = window.webkit.messageHandlers.bcAlbumPickerMode;
            if (!handler || typeof handler.postMessage !== 'function') {
                return;
            }

            handler.postMessage({
                mode: pickerMode || 'library'
            });
        },
        triggerComposerPicker (pickerType) {
            const refName = pickerType === 'camera' ? 'cameraInput' : 'libraryInput';
            const input = this.$refs[refName];

            if (input) {
                this.notifyNativePickerMode(pickerType);
                input.click();
            }
        },
        releaseObjectUrl (url) {
            if (typeof url !== 'string' || !url.startsWith('blob:') || typeof URL === 'undefined' || typeof URL.revokeObjectURL !== 'function') {
                return;
            }

            URL.revokeObjectURL(url);
        },
        releaseComposerAssetObjectUrls (asset) {
            if (!asset) {
                return;
            }

            const releasableUrls = new Set([
                asset.previewUrl,
                asset.thumbnailUrl
            ].filter(url => typeof url === 'string' && url.startsWith('blob:')));

            releasableUrls.forEach(url => {
                this.releaseObjectUrl(url);
            });
        },
        releaseComposerAssets (assets = []) {
            assets.forEach(asset => {
                this.releaseComposerAssetObjectUrls(asset);
            });
        },
        releaseSmartCandidateObjectUrls (candidate) {
            if (!candidate) {
                return;
            }

            const releasableUrls = new Set([
                candidate.previewUrl,
                candidate.videoPreviewUrl
            ].filter(url => typeof url === 'string' && url.startsWith('blob:')));

            releasableUrls.forEach(url => {
                this.releaseObjectUrl(url);
            });
        },
        releaseSmartCandidates (candidates = []) {
            candidates.forEach(candidate => {
                this.releaseSmartCandidateObjectUrls(candidate);
            });
        },
        releaseEntryObjectUrls (entry) {
            if (!entry || entry.persisted !== false) {
                return;
            }

            const releasableUrls = new Set([
                ...(Array.isArray(entry.images) ? entry.images : []),
                entry.thumbnail,
                entry.videoUrl
            ].filter(url => typeof url === 'string' && url.startsWith('blob:')));

            releasableUrls.forEach(url => {
                this.releaseObjectUrl(url);
            });
        },
        async handleLibraryInputChange (event) {
            if (this.activeComposer === 'smart-upload') {
                await this.handleSmartUploadFiles(event.target.files);
            } else {
                await this.handleComposerFiles(event.target.files);
            }
            event.target.value = '';
        },
        async handleCameraInputChange (event) {
            await this.handleComposerFiles(event.target.files);
            event.target.value = '';
        },
        async handleComposerFiles (fileList) {
            const files = Array.from(fileList || []).map(normalizeMediaFile);

            if (!files.length) {
                return;
            }

            if (this.activeComposer === 'photo') {
                const imageFiles = files.filter(file => resolveMediaKind(file) === 'image');
                const videoFiles = files.filter(file => resolveMediaKind(file) === 'video');

                if (imageFiles.length && videoFiles.length) {
                    this.$toast('一次仅支持选择多张照片，或选择多段视频');
                    return;
                }

                const nextMediaType = videoFiles.length ? 'video' : 'photo';
                const acceptedFiles = nextMediaType === 'video' ? videoFiles : imageFiles;

                if (!acceptedFiles.length) {
                    this.$toast('请选择图片或视频文件');
                    return;
                }

                this.composerForm.mediaType = nextMediaType;
                const nextAssets = [];

                try {
                    for (let index = 0; index < acceptedFiles.length; index += 1) {
                        const asset = await this.buildComposerAsset(acceptedFiles[index], nextMediaType);
                        nextAssets.push(asset);
                    }
                    this.releaseComposerAssets(this.composerAssets);
                    this.composerAssets = nextAssets;
                } catch (error) {
                    this.releaseComposerAssets(nextAssets);
                    this.$toast(error.message || '读取素材失败');
                }

                return;
            }

            const expectedType = this.composerForm.mediaType;
            const acceptedFiles = files.filter(file => {
                const fileMajorType = resolveMediaKind(file);
                return fileMajorType === (expectedType === 'video' ? 'video' : 'image');
            });

            if (!acceptedFiles.length) {
                this.$toast(expectedType === 'video' ? '请选择视频文件' : '请选择图片文件');
                return;
            }

            const nextFiles = acceptedFiles;
            const nextAssets = [];

            try {
                for (let index = 0; index < nextFiles.length; index += 1) {
                    const asset = await this.buildComposerAsset(nextFiles[index], expectedType);
                    nextAssets.push(asset);
                }

                if (this.activeComposer === 'camera') {
                    this.composerAssets = [...this.composerAssets, ...nextAssets];
                } else {
                    this.releaseComposerAssets(this.composerAssets);
                    this.composerAssets = nextAssets;
                }
            } catch (error) {
                this.releaseComposerAssets(nextAssets);
                this.$toast(error.message || '读取素材失败');
            }
        },
        buildVideoAsset (file, objectUrl) {
            return new Promise((resolve) => {
                const video = document.createElement('video');
                let settled = false;
                let durationSeconds = 0;
                let durationText = '00:00';
                let width = 0;
                let height = 0;
                let fallbackTimer = 0;

                const cleanup = () => {
                    if (fallbackTimer) {
                        window.clearTimeout(fallbackTimer);
                        fallbackTimer = 0;
                    }
                    video.onloadeddata = null;
                    video.onloadedmetadata = null;
                    video.onseeked = null;
                    video.onerror = null;
                    video.pause();
                    video.removeAttribute('src');
                    video.load();
                };

                const resolveAsset = (thumbnailUrl = '') => {
                    if (settled) {
                        return;
                    }

                    settled = true;
                    cleanup();
                    resolve({
                        id: createLocalAssetId(),
                        kind: 'video',
                        file,
                        previewUrl: objectUrl,
                        thumbnailUrl: thumbnailUrl || buildVideoPlaceholderThumbnail(durationText),
                        durationText,
                        durationSeconds,
                        width,
                        height
                    });
                };

                const updateVideoMetadata = () => {
                    durationSeconds = Math.max(1, Math.round(video.duration || 0));
                    durationText = formatDurationText(durationSeconds);
                    width = video.videoWidth || 0;
                    height = video.videoHeight || 0;
                };

                const tryBuildFrameThumbnail = () => {
                    updateVideoMetadata();
                    try {
                        const canvas = document.createElement('canvas');
                        canvas.width = video.videoWidth || 360;
                        canvas.height = video.videoHeight || 640;
                        const context = canvas.getContext('2d');

                        if (!context) {
                            resolveAsset('');
                            return;
                        }

                        context.drawImage(video, 0, 0, canvas.width, canvas.height);
                        resolveAsset(canvas.toDataURL('image/jpeg', 0.86));
                    } catch (error) {
                        resolveAsset('');
                    }
                };

                video.preload = 'metadata';
                video.muted = true;
                video.playsInline = true;
                video.src = objectUrl;

                video.onloadedmetadata = () => {
                    updateVideoMetadata();
                    const seekTarget = Math.min(0.1, Math.max(0, Number(video.duration || 0) - 0.01));
                    if (Number.isFinite(seekTarget) && video.duration > 0) {
                        try {
                            video.currentTime = seekTarget;
                        } catch (error) {}
                    }

                    fallbackTimer = window.setTimeout(() => {
                        resolveAsset('');
                    }, 1200);
                };

                video.onloadeddata = () => {
                    tryBuildFrameThumbnail();
                };

                video.onseeked = () => {
                    tryBuildFrameThumbnail();
                };

                video.onerror = () => {
                    resolveAsset('');
                };
            });
        },
        buildPhotoAsset (file, objectUrl) {
            return new Promise((resolve, reject) => {
                const image = new Image();
                image.onload = () => {
                    resolve({
                        id: createLocalAssetId(),
                        kind: 'photo',
                        file,
                        previewUrl: objectUrl,
                        thumbnailUrl: objectUrl,
                        durationText: '',
                        width: image.naturalWidth || 0,
                        height: image.naturalHeight || 0
                    });
                };
                image.onerror = () => {
                    reject(new Error('读取图片失败'));
                };
                image.src = objectUrl;
            });
        },
        async buildComposerAsset (file, mediaType) {
            const objectUrl = URL.createObjectURL(file);

            try {
                if (mediaType === 'video') {
                    return await this.buildVideoAsset(file, objectUrl);
                }

                return await this.buildPhotoAsset(file, objectUrl);
            } catch (error) {
                this.releaseObjectUrl(objectUrl);
                throw error;
            }
        },
        async buildFileFingerprint (file) {
            const fallbackKey = [
                file.type || '',
                file.size || 0,
                file.lastModified || 0,
                (file.name || '').toLowerCase()
            ].join(':');

            if (!file || typeof file.slice !== 'function' || !window.crypto || !window.crypto.subtle) {
                return fallbackKey;
            }

            try {
                const sampleSize = 256 * 1024;
                const blobParts = [
                    file.slice(0, Math.min(sampleSize, file.size))
                ];

                if (file.size > sampleSize) {
                    blobParts.push(file.slice(Math.max(0, file.size - sampleSize), file.size));
                }

                const sampleBuffer = await new Blob(blobParts).arrayBuffer();
                const digest = await window.crypto.subtle.digest('SHA-256', sampleBuffer);

                return `${file.type || ''}:${file.size || 0}:${bufferToHex(digest)}`;
            } catch (error) {
                return fallbackKey;
            }
        },
        evaluateSmartCandidate (file, asset, takenAtTimestamp) {
            const reasons = [];
            let score = 0;
            const lowerName = (file.name || '').toLowerCase();

            if (/(screenshot|screen[_ -]?shot|截屏|截图)/.test(lowerName)) {
                score -= 5;
                reasons.push('疑似截图');
            }

            if (file.size >= 300 * 1024) {
                score += 2;
            } else if (file.size >= 80 * 1024) {
                score += 1;
            } else {
                score -= 2;
                reasons.push('文件偏小');
            }

            const width = Number(asset.width || 0);
            const height = Number(asset.height || 0);
            if (width && height) {
                const minEdge = Math.min(width, height);
                const maxEdge = Math.max(width, height);

                if (minEdge >= 720) {
                    score += 2;
                } else if (minEdge >= 480) {
                    score += 1;
                } else {
                    score -= 2;
                    reasons.push('分辨率偏低');
                }

                if (minEdge > 0 && maxEdge / minEdge <= 1.9) {
                    score += 1;
                } else {
                    score -= 1;
                    reasons.push('比例较极端');
                }
            }

            if (asset.kind === 'video') {
                const durationSeconds = Number(asset.durationSeconds || 0);
                if (durationSeconds >= 3 && durationSeconds <= 90) {
                    score += 2;
                } else if (durationSeconds >= 1 && durationSeconds <= 180) {
                    score += 1;
                } else {
                    score -= 2;
                    reasons.push('时长不适合首轮推荐');
                }
            }

            const ageDays = Math.floor((Date.now() - takenAtTimestamp) / 86400000);
            if (ageDays <= 120) {
                score += 1;
            }

            const autoSelected = score >= 2;
            if (!reasons.length) {
                reasons.push(autoSelected ? '画质和时长较适合直接导入' : '建议人工复核后再导入');
            }

            return {
                score,
                selected: autoSelected,
                qualityLabel: autoSelected ? '推荐导入' : '待人工确认',
                reasonText: reasons.slice(0, 2).join('，')
            };
        },
        compareSmartCandidatePriority (left, right) {
            if (right.score !== left.score) {
                return right.score - left.score;
            }

            const leftArea = Number(left.width || 0) * Number(left.height || 0);
            const rightArea = Number(right.width || 0) * Number(right.height || 0);
            if (rightArea !== leftArea) {
                return rightArea - leftArea;
            }

            if (right.file.size !== left.file.size) {
                return right.file.size - left.file.size;
            }

            return Number(right.takenAt || 0) - Number(left.takenAt || 0);
        },
        removeComposerAsset (assetId) {
            const removedAssets = this.composerAssets.filter(asset => asset.id === assetId);
            this.releaseComposerAssets(removedAssets);
            this.composerAssets = this.composerAssets.filter(asset => asset.id !== assetId);
        },
        async handleSmartUploadFiles (fileList) {
            const files = Array.from(fileList || []).map(normalizeMediaFile);
            this.smartUploadSummary = createSmartUploadSummary();
            this.releaseSmartCandidates(this.smartCandidates);
            this.smartCandidates = [];

            if (!files.length) {
                return;
            }

            const acceptedFiles = files.filter(file => {
                const fileMajorType = resolveMediaKind(file);
                return fileMajorType === 'image' || fileMajorType === 'video';
            });

            if (!acceptedFiles.length) {
                this.smartCandidates = [];
                this.$toast('请选择图片或视频文件');
                return;
            }

            const rawCandidates = [];
            try {
                for (let index = 0; index < acceptedFiles.length; index += 1) {
                    rawCandidates.push(await this.buildSmartCandidate(acceptedFiles[index]));
                }

                const groupedCandidates = new Map();
                rawCandidates.forEach(candidate => {
                    const matchedGroup = groupedCandidates.get(candidate.fingerprint) || [];
                    matchedGroup.push(candidate);
                    groupedCandidates.set(candidate.fingerprint, matchedGroup);
                });

                const dedupedCandidates = [];
                let dedupedCount = 0;

                groupedCandidates.forEach(group => {
                    group.sort(this.compareSmartCandidatePriority);
                    const keptCandidate = {
                        ...group[0],
                        duplicateCount: Math.max(0, group.length - 1)
                    };

                    dedupedCount += keptCandidate.duplicateCount;
                    dedupedCandidates.push(keptCandidate);
                });

                dedupedCandidates.sort((left, right) => {
                    if (Number(right.selected) !== Number(left.selected)) {
                        return Number(right.selected) - Number(left.selected);
                    }
                    if (right.score !== left.score) {
                        return right.score - left.score;
                    }
                    return Number(right.takenAt || 0) - Number(left.takenAt || 0);
                });

                this.smartCandidates = dedupedCandidates;
                this.smartUploadSummary = {
                    sourceCount: acceptedFiles.length,
                    dedupedCount,
                    recommendedCount: dedupedCandidates.filter(item => item.selected).length,
                    reviewCount: dedupedCandidates.filter(item => !item.selected).length
                };
            } catch (error) {
                this.releaseSmartCandidates(rawCandidates);
                this.smartUploadSummary = createSmartUploadSummary();
                this.$toast(error.message || '读取候选素材失败');
            }
        },
        async buildSmartCandidate (file) {
            const mediaType = resolveMediaKind(file) === 'video' ? 'video' : 'photo';
            const asset = await this.buildComposerAsset(file, mediaType);
            const takenAtTimestamp = Number(file.lastModified) || Date.now();
            const title = file.name.replace(/\.[^/.]+$/, '') || (mediaType === 'video' ? '新视频候选' : '新照片候选');
            const fingerprint = await this.buildFileFingerprint(file);
            const recommendation = this.evaluateSmartCandidate(file, asset, takenAtTimestamp);

            return {
                id: createLocalAssetId(),
                title,
                takenAtLabel: formatCandidateDateTime(takenAtTimestamp),
                source: '本机相册',
                location: '宝宝相册',
                mediaType,
                previewUrl: mediaType === 'video' ? (asset.thumbnailUrl || asset.previewUrl) : asset.previewUrl,
                selected: recommendation.selected,
                file,
                durationText: asset.durationText || '',
                videoPreviewUrl: mediaType === 'video' ? asset.previewUrl : '',
                takenAt: Math.floor(takenAtTimestamp / 1000),
                fingerprint,
                width: asset.width || 0,
                height: asset.height || 0,
                score: recommendation.score,
                qualityLabel: recommendation.qualityLabel,
                reasonText: recommendation.reasonText,
                duplicateCount: 0
            };
        },
        createLocalEntryFromComposer () {
            const timestamp = Date.now();
            const mediaType = this.composerForm.mediaType;
            const firstAsset = this.composerAssets[0];
            const takenAt = resolveFileTakenAt(firstAsset?.file);

            if (mediaType === 'video' && firstAsset) {
                return {
                    id: `local-video-${timestamp}`,
                    postType: 'video',
                    author: '妈妈',
                    relativeTime: buildLocalRelativeTime(takenAt),
                    caption: this.composerForm.caption || '刚保存了一段新视频',
                    location: this.composerForm.location || '宝宝相册',
                    uploadStatus: 'done',
                    uploadText: '仅本地预览',
                    images: [firstAsset.thumbnailUrl].filter(Boolean),
                    thumbnail: firstAsset.thumbnailUrl,
                    videoUrl: firstAsset.previewUrl,
                    videoDuration: firstAsset.durationText,
                    selectedReaction: '',
                    reactions: createReactionState(),
                    takenAt
                };
            }

            return {
                id: `local-photo-${timestamp}`,
                postType: 'photo',
                author: '妈妈',
                relativeTime: buildLocalRelativeTime(takenAt),
                caption: this.composerForm.caption || '刚保存了一组新照片',
                location: this.composerForm.location || '宝宝相册',
                uploadStatus: 'done',
                uploadText: '仅本地预览',
                images: this.composerAssets.map(asset => asset.previewUrl),
                selectedReaction: '',
                reactions: createReactionState(),
                takenAt
            };
        },
        createPendingUploadEntry ({ assets, caption, location, takenAt, author = '妈妈' }) {
            const isVideo = this.composerForm.mediaType === 'video';
            const firstAsset = assets[0];
            const totalAssets = assets.length;

            return {
                id: `local-upload-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
                postType: isVideo ? 'video' : 'photo',
                author,
                relativeTime: buildLocalRelativeTime(takenAt),
                caption: caption || (isVideo ? '刚记录了一段新视频' : '刚整理了一组新照片'),
                location: location || '宝宝相册',
                uploadStatus: 'uploading',
                uploadText: `已上传 0/${totalAssets}`,
                images: isVideo
                    ? [firstAsset.thumbnailUrl || firstAsset.previewUrl].filter(Boolean)
                    : assets.map(asset => asset.previewUrl),
                thumbnail: isVideo ? (firstAsset.thumbnailUrl || firstAsset.previewUrl || '') : '',
                videoUrl: isVideo ? firstAsset.previewUrl : '',
                videoDuration: isVideo ? firstAsset.durationText : '',
                selectedReaction: '',
                reactions: createReactionState(),
                takenAt,
                persisted: false,
                uploadProgress: createUploadProgress(totalAssets)
            };
        },
        createVideoPendingUploadEntry ({ asset, caption, location, takenAt, author = '妈妈' }) {
            return {
                id: `local-upload-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
                postType: 'video',
                author,
                relativeTime: buildLocalRelativeTime(takenAt),
                caption: caption || '刚记录了一段新视频',
                location: location || '宝宝相册',
                uploadStatus: 'uploading',
                uploadText: '已上传 0/1',
                images: [asset.thumbnailUrl || asset.previewUrl].filter(Boolean),
                thumbnail: asset.thumbnailUrl || asset.previewUrl || '',
                videoUrl: asset.previewUrl,
                videoDuration: asset.durationText || '',
                selectedReaction: '',
                reactions: createReactionState(),
                takenAt,
                persisted: false,
                uploadProgress: createUploadProgress(1)
            };
        },
        createUploadJob ({ entry, assets, caption, location, takenAt }) {
            return {
                id: createUploadJobId(),
                entryId: entry.id,
                postType: entry.postType,
                assets: assets.slice(),
                caption,
                location,
                takenAt,
                totalAssets: assets.length,
                completedAssets: 0,
                assetProgresses: assets.map(() => 0),
                status: 'uploading',
                videoDuration: entry.videoDuration || ''
            };
        },
        async uploadVideoThumbnailIfAvailable (thumbnailUrl, fallbackName = 'video-thumbnail.jpg') {
            const thumbnailFile = dataUrlToFile(thumbnailUrl, fallbackName);
            if (!thumbnailFile) {
                return '';
            }

            try {
                return await uploadBabyAlbumFile({
                    file: thumbnailFile,
                    mediaType: 'photo'
                });
            } catch (error) {
                return '';
            }
        },
        hasUploadJob (jobId) {
            return this.uploadJobs.some(job => job.id === jobId);
        },
        cancelUploadJobsByEntryId (entryId) {
            const normalizedEntryId = String(entryId);
            this.uploadJobs = this.uploadJobs.filter(job => String(job.entryId) !== normalizedEntryId);
        },
        patchEntryProgress (entryId, uploadProgress, extraPatch = {}) {
            const normalizedEntryId = String(entryId);

            this.groups = this.groups.map(group => ({
                ...group,
                entries: group.entries.map(entry => {
                    if (String(entry.id) !== normalizedEntryId) {
                        return entry;
                    }

                    return {
                        ...entry,
                        ...extraPatch,
                        uploadProgress: {
                            ...(entry.uploadProgress || createUploadProgress(uploadProgress.totalAssets)),
                            ...uploadProgress
                        }
                    };
                })
            }));
        },
        syncUploadJobProgressToEntry (job) {
            const weightedUploadedAssets = job.assetProgresses.reduce((sum, progress) => (
                sum + (Math.max(0, Math.min(100, Number(progress || 0))) / 100)
            ), 0);
            const progressPercent = job.totalAssets
                ? Math.max(1, Math.min(99, Math.round((weightedUploadedAssets / job.totalAssets) * 100)))
                : 0;
            const completedAssets = Math.max(0, Number(job.completedAssets || 0));

            this.patchEntryProgress(job.entryId, {
                totalAssets: job.totalAssets,
                completedAssets,
                uploadedAssets: completedAssets,
                pendingAssets: Math.max(0, job.totalAssets - completedAssets),
                progressPercent
            }, {
                uploadStatus: job.status === 'failed' ? 'failed' : 'uploading',
                uploadText: job.status === 'failed'
                    ? '上传失败'
                    : `已上传 ${completedAssets}/${job.totalAssets}`
            });
        },
        updateUploadJobProgress (jobId, assetIndex, progress) {
            const matchedIndex = this.uploadJobs.findIndex(job => job.id === jobId);
            if (matchedIndex === -1) {
                return;
            }

            const nextJobs = this.uploadJobs.slice();
            const nextJob = {
                ...nextJobs[matchedIndex],
                assetProgresses: nextJobs[matchedIndex].assetProgresses.slice()
            };
            nextJob.assetProgresses.splice(assetIndex, 1, Math.max(0, Math.min(100, Number(progress || 0))));
            nextJobs.splice(matchedIndex, 1, nextJob);
            this.uploadJobs = nextJobs;
            this.syncUploadJobProgressToEntry(nextJob);
        },
        markUploadAssetCompleted (jobId, assetIndex) {
            const matchedIndex = this.uploadJobs.findIndex(job => job.id === jobId);
            if (matchedIndex === -1) {
                return;
            }

            const nextJobs = this.uploadJobs.slice();
            const nextJob = {
                ...nextJobs[matchedIndex],
                assetProgresses: nextJobs[matchedIndex].assetProgresses.slice()
            };
            nextJob.assetProgresses.splice(assetIndex, 1, 100);
            nextJob.completedAssets = nextJob.assetProgresses.filter(progress => Number(progress) >= 100).length;
            nextJobs.splice(matchedIndex, 1, nextJob);
            this.uploadJobs = nextJobs;
            this.syncUploadJobProgressToEntry(nextJob);
        },
        replaceLocalEntry (entryId, nextEntry, focusDateId = 'all') {
            const currentEntry = this.findEntryById(entryId);
            this.releaseEntryObjectUrls(currentEntry);
            const nextEntries = this.collectLocalEntries(entryId);
            nextEntries.push({
                ...nextEntry,
                persisted: nextEntry.persisted !== false
            });
            this.setLocalEntries(nextEntries, focusDateId);
        },
        markUploadJobFailed (jobId, error) {
            const matchedIndex = this.uploadJobs.findIndex(job => job.id === jobId);
            if (matchedIndex === -1) {
                return;
            }

            const failedJob = {
                ...this.uploadJobs[matchedIndex],
                status: 'failed'
            };
            this.uploadJobs = this.uploadJobs.filter(job => job.id !== jobId);
            this.patchEntryProgress(failedJob.entryId, {
                totalAssets: failedJob.totalAssets,
                completedAssets: failedJob.completedAssets,
                uploadedAssets: failedJob.completedAssets,
                pendingAssets: Math.max(0, failedJob.totalAssets - failedJob.completedAssets),
                progressPercent: failedJob.totalAssets ? Math.round((failedJob.completedAssets / failedJob.totalAssets) * 100) : 0
            }, {
                uploadStatus: 'failed',
                uploadText: '上传失败'
            });
            this.$toast(error.message || '上传失败');
        },
        async processUploadJob (jobId) {
            const matchedJob = this.uploadJobs.find(job => job.id === jobId);
            if (!matchedJob) {
                return;
            }

            const uploadedUrls = [];

            try {
                if (matchedJob.postType === 'video') {
                    const videoAsset = matchedJob.assets[0];
                    const videoUrl = await uploadBabyAlbumFile({
                        file: videoAsset.file,
                        mediaType: 'video',
                        onProgress: progress => {
                            this.updateUploadJobProgress(jobId, 0, progress);
                        }
                    });

                    uploadedUrls.push(videoUrl);
                    this.markUploadAssetCompleted(jobId, 0);
                    if (!this.hasUploadJob(jobId)) {
                        return;
                    }

                    const thumbnailUrl = await this.uploadVideoThumbnailIfAvailable(
                        videoAsset.thumbnailUrl,
                        `${videoAsset.file?.name || 'baby-video'}-thumbnail.jpg`
                    );
                    const persistedEntry = await createBabyAlbumEntry({
                        token: this.token,
                        postType: 'video',
                        images: uploadedUrls,
                        caption: matchedJob.caption,
                        location: matchedJob.location,
                        videoDuration: matchedJob.videoDuration,
                        takenAt: matchedJob.takenAt,
                        thumbnail: thumbnailUrl || buildVideoCoverUrl(videoUrl),
                        babyId: this.currentBabyId
                    });
                    if (!this.hasUploadJob(jobId)) {
                        if (persistedEntry.id && this.token) {
                            try {
                                await deleteBabyAlbumEntry({
                                    token: this.token,
                                    id: persistedEntry.id,
                                    babyId: this.currentBabyId
                                });
                            } catch (deleteError) {}
                        }
                        return;
                    }

                    this.uploadJobs = this.uploadJobs.filter(job => job.id !== jobId);
                    this.replaceLocalEntry(matchedJob.entryId, persistedEntry, this.currentDate);
                    return;
                }

                for (let index = 0; index < matchedJob.assets.length; index += 1) {
                    const uploadedUrl = await uploadBabyAlbumFile({
                        file: matchedJob.assets[index].file,
                        mediaType: 'photo',
                        onProgress: progress => {
                            this.updateUploadJobProgress(jobId, index, progress);
                        }
                    });
                    uploadedUrls.push(uploadedUrl);
                    this.markUploadAssetCompleted(jobId, index);
                    if (!this.hasUploadJob(jobId)) {
                        return;
                    }
                }

                const persistedEntry = await createBabyAlbumEntry({
                    token: this.token,
                    postType: 'photo',
                    images: uploadedUrls,
                    caption: matchedJob.caption,
                    location: matchedJob.location,
                    takenAt: matchedJob.takenAt,
                    babyId: this.currentBabyId
                });
                if (!this.hasUploadJob(jobId)) {
                    if (persistedEntry.id && this.token) {
                        try {
                            await deleteBabyAlbumEntry({
                                token: this.token,
                                id: persistedEntry.id,
                                babyId: this.currentBabyId
                            });
                        } catch (deleteError) {}
                    }
                    return;
                }

                this.uploadJobs = this.uploadJobs.filter(job => job.id !== jobId);
                this.replaceLocalEntry(matchedJob.entryId, persistedEntry, this.currentDate);
            } catch (error) {
                this.markUploadJobFailed(jobId, error);
            }
        },
        enqueueComposerUpload () {
            if (this.composerForm.mediaType === 'video' && this.composerAssets.length > 1) {
                const nextJobs = [];
                this.composerAssets.forEach(asset => {
                    const takenAt = resolveFileTakenAt(asset.file);
                    const pendingEntry = this.createVideoPendingUploadEntry({
                        asset,
                        caption: this.composerForm.caption,
                        location: this.composerForm.location,
                        takenAt
                    });
                    const uploadJob = this.createUploadJob({
                        entry: pendingEntry,
                        assets: [asset],
                        caption: this.composerForm.caption,
                        location: this.composerForm.location,
                        takenAt
                    });

                    this.upsertLocalEntry(pendingEntry, this.currentDate);
                    nextJobs.push(uploadJob);
                });

                this.uploadJobs = [...nextJobs, ...this.uploadJobs];
                this.closeCreateFlow();
                this.$toast(`已加入 ${nextJobs.length} 条视频上传队列`);
                nextJobs.forEach(job => {
                    this.processUploadJob(job.id);
                });
                return;
            }

            const takenAt = resolveFileTakenAt(this.composerAssets[0]?.file);
            const pendingEntry = this.createPendingUploadEntry({
                assets: this.composerAssets,
                caption: this.composerForm.caption,
                location: this.composerForm.location,
                takenAt
            });
            const uploadJob = this.createUploadJob({
                entry: pendingEntry,
                assets: this.composerAssets,
                caption: this.composerForm.caption,
                location: this.composerForm.location,
                takenAt
            });

            this.upsertLocalEntry(pendingEntry, this.currentDate);
            this.uploadJobs = [uploadJob, ...this.uploadJobs];
            this.closeCreateFlow();
            this.$toast('已加入上传队列');
            this.processUploadJob(uploadJob.id);
        },
        openSearchHub () {
            this.activeMenuId = '';
            this.pushAlbumPath('/baby/album/search', {
                keepKeyword: true
            });
        },
        cancelSearchHub () {
            if (window.history.length > 1) {
                this.$router.back();
                return;
            }

            this.pushAlbumPath('/baby/album');
        },
        clearSearchHub () {
            this.searchHubKeyword = '';
            this.pushAlbumPath('/baby/album/search', {
                replace: true
            });
        },
        openDateExplorer () {
            this.pushAlbumPath('/baby/album/search/date', {
                keepKeyword: true
            });
        },
        openDatePickerFromResult () {
            this.pushAlbumPath('/baby/album/search/date', {
                keepKeyword: true,
                replace: true
            });
        },
        openPhotoExplorer () {
            this.pushAlbumPath('/baby/album/search/photo', {
                keepKeyword: true
            });
        },
        openVideoExplorer () {
            this.pushAlbumPath('/baby/album/search/video', {
                keepKeyword: true
            });
        },
        handleSearchHubSelection (key) {
            if (key === 'date') {
                this.openDateExplorer();
                return;
            }
            if (key === 'photo') {
                this.openPhotoExplorer();
                return;
            }
            this.openVideoExplorer();
        },
        clearDateFilter () {
            this.pushAlbumPath('/baby/album');
            this.activeMenuId = '';
        },
        goBack () {
            if (!this.isAlbumRootPage) {
                if (window.history.length > 1) {
                    this.$router.back();
                    return;
                }

                this.pushAlbumPath('/baby/album');
                return;
            }

            if (window.history.length > 1 && !this.useNativeAlbumNav) {
                this.$router.back();
                return;
            }

            this.$router.push({
                path: '/baby/home',
                query: {
                    ...this.$route.query
                }
            });
        },
        toggleSearch () {
            this.openSearchHub();
        },
        clearSearch () {
            this.searchHubKeyword = '';
        },
        selectFilter () {
            this.activeMenuId = '';
        },
        jumpToToday () {
            this.pushAlbumPath('/baby/album', {
                extraQuery: {
                    date: this.todayGroup.id
                }
            });
            this.activeMenuId = '';
        },
        closeMenu () {
            this.activeMenuId = '';
        },
        async confirmDelete (message) {
            if (this.$dialog && this.$dialog.confirm) {
                try {
                    await this.$dialog.confirm({
                        title: '确认删除',
                        message,
                        confirmButtonText: '删除',
                        confirmButtonColor: '#ee5253'
                    });
                    return true;
                } catch (error) {
                    return false;
                }
            }

            return typeof window !== 'undefined' && typeof window.confirm === 'function'
                ? window.confirm(message)
                : false;
        },
        selectDate (action) {
            this.currentDate = action.value;
            this.activeMenuId = '';
        },
        selectCalendarDay (cell) {
            if (!cell || cell.blank) {
                return;
            }

            if (!cell.hasEntries && !cell.isToday) {
                this.$toast('这一天还没有照片或视频');
                return;
            }

            this.pushAlbumPath('/baby/album/search/date', {
                keepKeyword: true,
                extraQuery: {
                    date: cell.id,
                    album_search: 'date'
                }
            });
        },
        handleMediaEmptyAction () {
            if (this.isDateResultGridPage) {
                this.openDatePickerFromResult();
                return;
            }

            this.openSearchHub();
        },
        toggleMenu (entryId) {
            this.postAlbumDebug({
                type: 'toggle_menu',
                entryId,
                previousActiveMenuId: this.activeMenuId || ''
            });
            this.activeMenuId = this.activeMenuId === entryId ? '' : entryId;
            this.postAlbumDebug({
                type: 'toggle_menu_done',
                entryId,
                activeMenuId: this.activeMenuId || ''
            });
        },
        previewEntry (entry, startPosition) {
            this.activeMenuId = '';

            if (entry.postType === 'video') {
                this.videoPreviewEntry = entry;
                this.showVideoPreviewPlayButton = false;
                this.prepareVideoPreviewUrl();
                this.$nextTick(this.playVideoPreview);
                return;
            }

            ImagePreview({
                images: entry.images,
                startPosition
            });
        },
        handleMediaItemTap (item) {
            if (!item) {
                return;
            }

            if (this.batchDeleteMode) {
                if (!item.selectable) {
                    return;
                }

                this.toggleMediaSelection(item.id);
                return;
            }

            this.previewEntry(item.entry, item.mediaIndex);
        },
        handleMediaThumbnailError (event, item) {
            if (!event?.target || item?.postType !== 'video') {
                return;
            }

            event.target.src = buildVideoPlaceholderThumbnail(item.videoDuration || '00:00');
            this.markMediaThumbnailLoaded(item.id);
        },
        markMediaThumbnailLoaded (itemId) {
            this.$set(this.loadedMediaThumbnailMap, itemId, true);
        },
        isMediaThumbnailLoaded (itemId) {
            return !!this.loadedMediaThumbnailMap[itemId];
        },
        openMediaActionSheet () {
            this.showMediaActionSheet = true;
        },
        closeMediaActionSheet () {
            this.showMediaActionSheet = false;
        },
        enterBatchDeleteMode () {
            this.showMediaActionSheet = false;
            this.batchDeleteMode = true;
            this.selectedMediaIds = [];
        },
        cancelBatchDeleteMode () {
            this.batchDeleteMode = false;
            this.batchDeletePending = false;
            this.selectedMediaIds = [];
        },
        toggleMediaSelection (itemId) {
            if (this.selectedMediaIds.includes(itemId)) {
                this.selectedMediaIds = this.selectedMediaIds.filter(id => id !== itemId);
                return;
            }

            this.selectedMediaIds = [...this.selectedMediaIds, itemId];
        },
        isMediaSelected (itemId) {
            return this.selectedMediaIds.includes(itemId);
        },
        async confirmBatchDelete () {
            if (!this.selectedMediaIds.length || this.batchDeletePending) {
                return;
            }

            this.postAlbumDebug({
                type: 'batch_remove_start',
                selectedCount: this.selectedMediaIds.length,
                mediaType: this.currentMediaTypeLabel
            });
            const confirmed = await this.confirmDelete(`确认删除已选 ${this.selectedMediaIds.length} 项${this.currentMediaTypeLabel}吗？`);
            this.postAlbumDebug({
                type: 'batch_remove_confirmed',
                selectedCount: this.selectedMediaIds.length,
                confirmed
            });
            if (!confirmed) {
                return;
            }

            this.batchDeletePending = true;
            const selectedItems = this.currentMediaItems.filter(item => this.selectedMediaIds.includes(item.id));
            const groupedItems = new Map();

            selectedItems.forEach(item => {
                const matchedItems = groupedItems.get(item.entryId) || [];
                matchedItems.push(item);
                groupedItems.set(item.entryId, matchedItems);
            });

            try {
                for (const [entryId, entryItems] of groupedItems.entries()) {
                    const entry = this.findEntryById(entryId);
                    if (!entry) {
                        continue;
                    }

                    if (entry.postType === 'video') {
                        if (entry.persisted && this.token) {
                            await deleteBabyAlbumEntry({
                                token: this.token,
                                id: entry.id,
                                babyId: this.currentBabyId
                            });
                        }
                        this.removeEntryFromGroups(entry.id);
                        continue;
                    }

                    const selectedIndexes = new Set(entryItems.map(item => Number(item.mediaIndex)));
                    const remainingImages = (Array.isArray(entry.images) ? entry.images : [])
                        .filter((image, index) => !selectedIndexes.has(index));

                    if (!remainingImages.length) {
                        if (entry.persisted && this.token) {
                            await deleteBabyAlbumEntry({
                                token: this.token,
                                id: entry.id,
                                babyId: this.currentBabyId
                            });
                        }
                        this.removeEntryFromGroups(entry.id);
                        continue;
                    }

                    if (entry.persisted && this.token) {
                        const updatedEntry = await updateBabyAlbumEntry({
                            token: this.token,
                            id: entry.id,
                            caption: entry.caption,
                            location: entry.location,
                            takenAt: entry.takenAt,
                            images: remainingImages,
                            babyId: this.currentBabyId
                        });
                        this.replaceLocalEntry(entry.id, updatedEntry, this.currentDate);
                    } else {
                        this.upsertLocalEntry({
                            ...entry,
                            images: remainingImages
                        }, this.currentDate);
                    }
                }

                this.$toast(`已删除 ${selectedItems.length} 项${this.currentMediaTypeLabel}`);
                this.cancelBatchDeleteMode();
            } catch (error) {
                this.postAlbumDebug({
                    type: 'batch_remove_failed',
                    message: error.message || '批量删除失败'
                });
                this.$toast(error.message || '批量删除失败');
                this.batchDeletePending = false;
            }
        },
        closeVideoPreview () {
            const video = Array.isArray(this.$refs.videoPreviewEl)
                ? this.$refs.videoPreviewEl[0]
                : this.$refs.videoPreviewEl;
            if (video) {
                video.pause();
            }
            this.videoPreviewEntry = null;
            this.releaseVideoPreviewBlobUrl();
            this.showVideoPreviewPlayButton = false;
        },
        releaseVideoPreviewBlobUrl () {
            if (this.videoPreviewBlobUrl && typeof URL !== 'undefined') {
                URL.revokeObjectURL(this.videoPreviewBlobUrl);
            }
            this.videoPreviewBlobUrl = '';
            this.videoPreviewBlobSourceUrl = '';
            this.videoPreviewBlobLoading = false;
        },
        async prepareVideoPreviewUrl () {
            const sourceUrl = this.videoPreviewEntry?.videoUrl || '';
            if (!sourceUrl || !isQuickTimeCompatibilityUrl(sourceUrl)) {
                this.releaseVideoPreviewBlobUrl();
                return;
            }
            if (
                this.videoPreviewBlobSourceUrl === sourceUrl
                || this.videoPreviewBlobLoading
                || typeof fetch !== 'function'
                || typeof URL === 'undefined'
            ) {
                return;
            }

            this.videoPreviewBlobLoading = true;
            try {
                const response = await fetch(sourceUrl, { mode: 'cors', cache: 'force-cache' });
                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}`);
                }
                const sourceBlob = await response.blob();
                if (this.videoPreviewEntry?.videoUrl !== sourceUrl) {
                    return;
                }
                const playableBlob = new Blob([sourceBlob], {
                    type: sourceBlob.type && sourceBlob.type !== 'application/octet-stream'
                        ? sourceBlob.type
                        : 'video/quicktime'
                });
                this.releaseVideoPreviewBlobUrl();
                this.videoPreviewBlobUrl = URL.createObjectURL(playableBlob);
                this.videoPreviewBlobSourceUrl = sourceUrl;
                this.$nextTick(this.playVideoPreview);
            } catch (error) {
                this.postAlbumDebug({
                    type: 'video_preview_blob_failed',
                    entryId: this.videoPreviewEntry?.id || '',
                    message: error?.message || String(error || ''),
                    src: sourceUrl
                });
                this.showVideoPreviewPlayButton = true;
            } finally {
                this.videoPreviewBlobLoading = false;
            }
        },
        playVideoPreview () {
            const video = Array.isArray(this.$refs.videoPreviewEl)
                ? this.$refs.videoPreviewEl[0]
                : this.$refs.videoPreviewEl;
            if (!video || !this.activeVideoPreviewUrl) {
                return;
            }

            video.playsInline = true;
            video.setAttribute('playsinline', 'playsinline');
            video.setAttribute('webkit-playsinline', 'webkit-playsinline');
            if (video.networkState === 0) {
                try {
                    video.load();
                } catch (error) {}
            }
            video.play()
                .then(() => {
                    this.showVideoPreviewPlayButton = false;
                    this.postAlbumDebug({
                        type: 'video_preview_playing',
                        entryId: this.videoPreviewEntry?.id || ''
                    });
                })
                .catch(error => {
                    this.showVideoPreviewPlayButton = true;
                    this.postAlbumDebug({
                        type: 'video_preview_play_failed',
                        entryId: this.videoPreviewEntry?.id || '',
                        message: error?.message || String(error || ''),
                        src: video.currentSrc || video.src || ''
                    });
                });
        },
        handleVideoPreviewError () {
            const video = Array.isArray(this.$refs.videoPreviewEl)
                ? this.$refs.videoPreviewEl[0]
                : this.$refs.videoPreviewEl;
            if (isQuickTimeCompatibilityUrl(this.videoPreviewEntry?.videoUrl) && !this.videoPreviewBlobUrl) {
                this.prepareVideoPreviewUrl();
            }
            this.showVideoPreviewPlayButton = true;
            this.postAlbumDebug({
                type: 'video_preview_error',
                entryId: this.videoPreviewEntry?.id || '',
                code: video?.error?.code || 0,
                message: video?.error?.message || '',
                src: video?.currentSrc || video?.src || ''
            });
        },
        mockPlayVideo () {
            this.$toast('视频地址缺失，暂无法播放');
        },
        openCreatePanel () {
            this.activeMenuId = '';
            this.showMediaActionSheet = false;
            this.batchDeleteMode = false;
            this.selectedMediaIds = [];
            this.editingEntryId = '';
            this.resetComposerState();
            this.showCreatePanel = true;
        },
        closeCreateFlow () {
            this.showCreatePanel = false;
            this.resetComposerState();
        },
        handleComposerCancel () {
            if (this.activeComposer === 'edit' || !this.showCreatePanel) {
                this.resetComposerState();
                return;
            }

            this.resetComposerState();
        },
        toggleSmartCandidateSelection (candidateId) {
            this.smartCandidates = this.smartCandidates.map(candidate => (
                candidate.id === candidateId
                    ? { ...candidate, selected: !candidate.selected }
                    : candidate
            ));
        },
        selectRecommendedSmartCandidates () {
            this.smartCandidates = this.smartCandidates.map(candidate => ({
                ...candidate,
                selected: candidate.qualityLabel === '推荐导入'
            }));
        },
        clearSmartCandidateSelection () {
            this.smartCandidates = this.smartCandidates.map(candidate => ({
                ...candidate,
                selected: false
            }));
        },
        openComposerByQuery (compose) {
            const allowedTypes = ['camera', 'photo', 'smart-upload'];

            if (!allowedTypes.includes(compose)) {
                this.consumeComposeQuery();
                return;
            }

            this.openComposer(compose, false);
            this.consumeComposeQuery();
        },
        consumeComposeQuery () {
            const nextQuery = { ...this.$route.query };
            delete nextQuery.compose;
            this.$router.replace({
                path: this.$route.path,
                query: nextQuery
            }).catch(() => {});
        },
        openComposer (type) {
            this.showCreatePanel = true;
            this.activeMenuId = '';
            this.editingEntryId = '';
            this.activeComposer = type;
            this.releaseComposerAssets(this.composerAssets);
            this.releaseSmartCandidates(this.smartCandidates);
            this.composerForm = buildComposerDraft(type);
            this.composerAssets = [];
            this.smartCandidates = [];
            this.smartUploadSummary = createSmartUploadSummary();
            this.showEditTimePicker = false;

            if (type === 'camera') {
                this.composerForm.mediaType = 'video';
            }
        },
        resetComposerState () {
            this.releaseComposerAssets(this.composerAssets);
            this.releaseSmartCandidates(this.smartCandidates);
            this.activeComposer = '';
            this.editingEntryId = '';
            this.composerAssets = [];
            this.smartCandidates = [];
            this.smartUploadSummary = createSmartUploadSummary();
            this.showEditTimePicker = false;
            this.isSubmittingComposer = false;
        },
        applyLocalPrototypeEntry (entry) {
            this.prependEntry({
                ...entry,
                persisted: false
            });
        },
        async submitComposer () {
            if (!this.composerAssets.length || this.isSubmittingComposer) {
                return;
            }

            this.isSubmittingComposer = true;

            try {
                if (this.token) {
                    this.enqueueComposerUpload();
                    return;
                }

                if (this.composerForm.mediaType === 'video' && this.composerAssets.length > 1) {
                    this.composerAssets.forEach((asset, index) => {
                        const takenAt = resolveFileTakenAt(asset.file);
                        this.applyLocalPrototypeEntry({
                            id: `local-video-${Date.now()}-${index}`,
                            postType: 'video',
                            author: '妈妈',
                            relativeTime: buildLocalRelativeTime(takenAt),
                            caption: this.composerForm.caption || '刚保存了一段新视频',
                            location: this.composerForm.location || '宝宝相册',
                            uploadStatus: 'done',
                            uploadText: '仅本地预览',
                            images: [asset.thumbnailUrl].filter(Boolean),
                            thumbnail: asset.thumbnailUrl,
                            videoUrl: asset.previewUrl,
                            videoDuration: asset.durationText,
                            selectedReaction: '',
                            reactions: createReactionState(),
                            takenAt
                        });
                    });
                } else {
                    this.applyLocalPrototypeEntry(this.createLocalEntryFromComposer());
                }
                this.closeCreateFlow();
                this.$toast('已加入宝宝时间轴');
            } catch (error) {
                this.$toast(error.message || '发布失败');
            } finally {
                this.isSubmittingComposer = false;
            }
        },
        prependEntry (entry) {
            this.upsertLocalEntry(entry);
        },
        async importSmartCandidates () {
            const selectedItems = this.smartCandidates.filter(item => item.selected);

            if (!selectedItems.length || this.isSubmittingComposer) {
                return;
            }

            this.isSubmittingComposer = true;
            const focusDateId = selectedItems.length === 1
                ? resolveDateIdFromTimestamp(selectedItems[0].takenAt)
                : 'all';

            try {
                if (this.token) {
                    for (let index = 0; index < selectedItems.length; index += 1) {
                        const candidate = selectedItems[index];
                        if (candidate.mediaType === 'video') {
                            const videoUrl = await uploadBabyAlbumFile({
                                file: candidate.file,
                                mediaType: 'video'
                            });
                            const thumbnailUrl = await this.uploadVideoThumbnailIfAvailable(
                                candidate.previewUrl,
                                `${candidate.file?.name || 'baby-video'}-thumbnail.jpg`
                            );

                            await createBabyAlbumEntry({
                                token: this.token,
                                postType: 'video',
                                images: [videoUrl],
                                caption: candidate.title,
                                location: candidate.location,
                                videoDuration: candidate.durationText,
                                takenAt: candidate.takenAt,
                                thumbnail: thumbnailUrl || buildVideoCoverUrl(videoUrl),
                                babyId: this.currentBabyId
                            });
                            continue;
                        }

                        const uploadedUrl = await uploadBabyAlbumFile({
                            file: candidate.file,
                            mediaType: 'photo'
                        });

                        await createBabyAlbumEntry({
                            token: this.token,
                            postType: 'photo',
                            images: [uploadedUrl],
                            caption: candidate.title,
                            location: candidate.location,
                            takenAt: candidate.takenAt,
                            babyId: this.currentBabyId
                        });
                    }

                    await this.loadAlbumGroups({
                        preserveVisibleState: true
                    });
                    this.currentDate = focusDateId;
                } else {
                    selectedItems
                        .slice()
                        .reverse()
                        .forEach(candidate => {
                            if (candidate.mediaType === 'video') {
                                this.applyLocalPrototypeEntry({
                                    id: `local-smart-${candidate.id}-${Date.now()}`,
                                    postType: 'video',
                                    author: '妈妈',
                                    relativeTime: buildLocalRelativeTime(candidate.takenAt),
                                    caption: candidate.title,
                                    location: candidate.location,
                                    uploadStatus: 'done',
                                    uploadText: '仅本地预览',
                                    images: [candidate.previewUrl],
                                    thumbnail: candidate.previewUrl,
                                    videoUrl: candidate.videoPreviewUrl,
                                    videoDuration: candidate.durationText,
                                    selectedReaction: '',
                                    reactions: createReactionState(),
                                    takenAt: candidate.takenAt
                                });
                                return;
                            }

                            this.applyLocalPrototypeEntry({
                                id: `local-smart-${candidate.id}-${Date.now()}`,
                                postType: 'photo',
                                author: '妈妈',
                                relativeTime: buildLocalRelativeTime(candidate.takenAt),
                                caption: candidate.title,
                                location: candidate.location,
                                uploadStatus: 'done',
                                uploadText: '仅本地预览',
                                images: [candidate.previewUrl],
                                selectedReaction: '',
                                reactions: createReactionState(),
                                takenAt: candidate.takenAt
                            });
                        });

                    this.currentDate = focusDateId;
                }

                this.smartCandidates = [];
                this.closeCreateFlow();
                this.$toast(this.token
                    ? `已导入 ${selectedItems.length} 条素材`
                    : `已加入 ${selectedItems.length} 条候选素材`);
            } catch (error) {
                this.$toast(error.message || '导入失败');
            } finally {
                this.isSubmittingComposer = false;
            }
        },
        openEdit (entry) {
            this.activeMenuId = '';
            this.editingEntryId = entry.id;
            this.editDraft = {
                caption: entry.caption || '',
                location: entry.location || '',
                takenAt: entry.takenAt || Math.floor(Date.now() / 1000),
                images: entry.postType === 'photo' ? [...entry.images] : [],
                thumbnail: entry.thumbnail || entry.images[0] || ''
            };
            this.editPickerDate = createDateFromTimestamp(this.editDraft.takenAt);
            this.activeComposer = 'edit';
        },
        openEditTimePicker () {
            this.editPickerDate = createDateFromTimestamp(this.editDraft.takenAt);
            this.showEditTimePicker = true;
        },
        handleEditTimeConfirm (value) {
            const nextDate = new Date(value);
            this.editPickerDate = nextDate;
            this.editDraft.takenAt = Math.floor(nextDate.getTime() / 1000);
            this.showEditTimePicker = false;
        },
        handleEditTimeCancel () {
            this.showEditTimePicker = false;
        },
        moveEditAsset (index, direction) {
            if (!this.editingEntry || this.editingEntry.postType !== 'photo') {
                return;
            }

            const targetIndex = index + direction;
            if (targetIndex < 0 || targetIndex >= this.editDraft.images.length) {
                return;
            }

            this.editDraft.images = moveArrayItem(this.editDraft.images, index, targetIndex);
        },
        collectLocalEntries (excludedEntryId = null) {
            const normalizedExcludedId = excludedEntryId === null ? null : String(excludedEntryId);
            const nextEntries = [];

            this.groups.forEach(group => {
                group.entries.forEach(entry => {
                    if (normalizedExcludedId !== null && String(entry.id) === normalizedExcludedId) {
                        return;
                    }

                    nextEntries.push({
                        ...entry,
                        images: Array.isArray(entry.images) ? entry.images.slice() : [],
                        reactions: {
                            ...createReactionState(),
                            ...(entry.reactions || {})
                        }
                    });
                });
            });

            return nextEntries;
        },
        buildLocalGroupMeta (dateId, ageTextMap) {
            const [year, month, day] = dateId.split('-').map(item => Number(item));
            const groupDate = new Date(year, month - 1, day);
            const todayId = formatDateId();
            const yesterdayId = formatDateId(new Date(Date.now() - 86400000));

            return {
                id: dateId,
                label: dateId === todayId ? '今天' : (dateId === yesterdayId ? '昨天' : formatDisplayDate(groupDate)),
                displayDate: formatDisplayDate(groupDate),
                ageText: ageTextMap[dateId] || '',
                entries: []
            };
        },
        setLocalEntries (entries, focusDateId = this.currentDate) {
            const ageTextMap = this.groups.reduce((result, group) => {
                if (group.ageText) {
                    result[group.id] = group.ageText;
                }
                return result;
            }, {});
            const groupedMap = new Map();
            const nextGroups = [];

            entries
                .slice()
                .sort((left, right) => Number(right.takenAt || 0) - Number(left.takenAt || 0))
                .forEach(entry => {
                    const safeTakenAt = Number(entry.takenAt || Math.floor(Date.now() / 1000));
                    const dateId = resolveDateIdFromTimestamp(safeTakenAt);

                    if (!groupedMap.has(dateId)) {
                        const nextGroup = this.buildLocalGroupMeta(dateId, ageTextMap);
                        groupedMap.set(dateId, nextGroup);
                        nextGroups.push(nextGroup);
                    }

                    groupedMap.get(dateId).entries.push({
                        ...entry,
                        takenAt: safeTakenAt,
                        relativeTime: buildLocalRelativeTime(safeTakenAt),
                        images: Array.isArray(entry.images) ? entry.images.slice() : [],
                        reactions: {
                            ...createReactionState(),
                            ...(entry.reactions || {})
                        }
                    });
                });

            this.groups = nextGroups;
            this.currentDate = focusDateId || 'all';
            this.writeAlbumSnapshot();
            this.syncStickyShellHeight();
        },
        upsertLocalEntry (entry, focusDateId = this.currentDate) {
            const safeTakenAt = Number(entry.takenAt || Math.floor(Date.now() / 1000));
            const nextEntries = this.collectLocalEntries(entry.id);
            nextEntries.push({
                ...entry,
                takenAt: safeTakenAt
            });
            this.setLocalEntries(nextEntries, focusDateId);
        },
        async saveEdit () {
            const entry = this.findEntryById(this.editingEntryId);

            if (!entry || this.isSubmittingComposer) {
                return;
            }

            const nextTakenAt = Number(this.editDraft.takenAt || entry.takenAt || Math.floor(Date.now() / 1000));
            const nextImages = entry.postType === 'photo' ? this.editDraft.images.slice() : [];
            const unchanged = entry.caption === this.editDraft.caption
                && entry.location === this.editDraft.location
                && Number(entry.takenAt || 0) === nextTakenAt
                && (entry.postType !== 'photo' || areStringArraysEqual(entry.images || [], nextImages));

            if (unchanged) {
                this.resetComposerState();
                this.$toast('未修改内容');
                return;
            }

            this.isSubmittingComposer = true;

            try {
                if (entry.persisted && this.token) {
                    const updatedEntry = await updateBabyAlbumEntry({
                        token: this.token,
                        id: entry.id,
                        caption: this.editDraft.caption,
                        location: this.editDraft.location,
                        takenAt: nextTakenAt,
                        images: entry.postType === 'photo' ? nextImages : undefined,
                        babyId: this.currentBabyId
                    });

                    this.replaceLocalEntry(entry.id, updatedEntry, resolveDateIdFromTimestamp(nextTakenAt));
                } else {
                    this.upsertLocalEntry({
                        ...entry,
                        caption: this.editDraft.caption,
                        location: this.editDraft.location,
                        takenAt: nextTakenAt,
                        images: entry.postType === 'photo' ? nextImages : entry.images
                    });
                }

                this.resetComposerState();
                this.$toast('已保存修改');
            } catch (error) {
                this.$toast(error.message || '保存修改失败');
            } finally {
                this.isSubmittingComposer = false;
            }
        },
        removeEntryFromGroups (entryId) {
            const normalizedEntryId = String(entryId);
            const removedEntries = [];

            this.groups = this.groups
                .map(group => ({
                    ...group,
                    entries: group.entries.filter(item => {
                        const shouldRemove = String(item.id) === normalizedEntryId;
                        if (shouldRemove) {
                            removedEntries.push(item);
                        }
                        return !shouldRemove;
                    })
                }))
                .filter(group => group.entries.length);

            removedEntries.forEach(entry => {
                this.releaseEntryObjectUrls(entry);
            });
            this.cancelUploadJobsByEntryId(entryId);
            this.writeAlbumSnapshot();
            this.syncStickyShellHeight();
        },
        async removeEntry (entry) {
            this.activeMenuId = '';
            this.postAlbumDebug({
                type: 'remove_entry_start',
                entryId: entry?.id || '',
                persisted: !!entry?.persisted,
                hasToken: !!this.token
            });

            const confirmed = await this.confirmDelete('确认删除这条动态吗？');
            this.postAlbumDebug({
                type: 'remove_entry_confirmed',
                entryId: entry?.id || '',
                confirmed
            });
            if (!confirmed) {
                return;
            }

            if (entry.persisted && this.token) {
                try {
                    await deleteBabyAlbumEntry({
                        token: this.token,
                        id: entry.id,
                        babyId: this.currentBabyId
                    });
                } catch (error) {
                    this.postAlbumDebug({
                        type: 'remove_entry_api_failed',
                        entryId: entry?.id || '',
                        message: error.message || '删除失败'
                    });
                    this.$toast(error.message || '删除失败');
                    return;
                }
            }

            this.removeEntryFromGroups(entry.id);
            this.postAlbumDebug({
                type: 'remove_entry_done',
                entryId: entry?.id || ''
            });
            this.$toast('已删除该动态');
        },
        findEntryById (entryId) {
            for (let groupIndex = 0; groupIndex < this.groups.length; groupIndex += 1) {
                const group = this.groups[groupIndex];
                const matchedEntry = group.entries.find(entry => String(entry.id) === String(entryId));
                if (matchedEntry) {
                    return matchedEntry;
                }
            }

            return null;
        }
    }
};
</script>

<style lang="scss" scoped>
.baby-album-page {
    --album-page-top-pad: 14px;
    --album-page-side-pad: 12px;
    box-sizing: border-box;
    min-height: 100vh;
    min-height: 100dvh;
    padding: 0 var(--album-page-side-pad) calc(110px + env(safe-area-inset-bottom));
    background-color: #f6f3ee;
    background-image:
        radial-gradient(circle at top left, rgba(255, 245, 184, 0.5), transparent 32%),
        linear-gradient(180deg, #92ebd6 0, #92ebd6 92px, #f6f3ee 92px, #f6f3ee 100%);
    background-repeat: no-repeat;
    background-size: 100% 100%;
    font-family: 'SF Pro Text', 'PingFang SC', 'Helvetica Neue', Arial, sans-serif;
    isolation: isolate;
    transform: translateZ(0);
}

.native-nav-page {
    --album-page-top-pad: 10px;
}

.wechat-browser-page {
    --album-page-top-pad: 10px;
}

.search-page-shell {
    background: #fff;
}

.album-sticky-shell {
    position: fixed;
    left: 0;
    right: 0;
    top: 0;
    z-index: 24;
    box-sizing: border-box;
    padding: var(--album-page-top-pad) var(--album-page-side-pad) 12px;
    background: rgba(246, 243, 238, 0.92);
    box-shadow: 0 14px 28px rgba(28, 38, 54, 0.08);
    backdrop-filter: blur(18px);
    overflow: hidden;
    contain: layout paint;
    transform: translateZ(0);
}

.native-nav-page .album-sticky-shell {
    max-height: 238px;
    background: #f6f3ee;
    box-shadow: 0 10px 20px rgba(28, 38, 54, 0.04);
    backdrop-filter: none;
}

.album-sticky-spacer {
    width: 100%;
    margin-bottom: 16px;
    max-height: 300px;
    overflow: hidden;
}

.native-nav-page .album-sticky-spacer {
    max-height: 238px;
}

.album-home-shell {
    gap: 12px;
}

.home-header {
    border-radius: 20px 20px 0 0;
}

.header-search-compact {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 38px;
    height: 38px;
    border: 0;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.86);
    color: #2b6f77;
    font-size: 18px;
    font-weight: 700;
    box-shadow: inset 0 0 0 1px rgba(5, 194, 203, 0.14);
}

.home-collapsible-panel {
    max-height: 176px;
    overflow: hidden;
    opacity: 1;
    transform: translateY(0);
    transition:
        max-height 0.24s ease,
        opacity 0.2s ease,
        transform 0.24s ease;
}

.home-chrome-collapsed .home-collapsible-panel {
    max-height: 0;
    opacity: 0;
    transform: translateY(-10px);
    pointer-events: none;
}

.native-nav-page .album-home-shell.home-chrome-collapsed {
    max-height: 0;
    padding-top: 0;
    padding-bottom: 0;
    background: transparent;
    box-shadow: none;
    backdrop-filter: none;
    pointer-events: none;
}

.search-entry-card {
    display: grid;
    grid-template-columns: auto 1fr auto;
    align-items: center;
    gap: 12px;
    width: 100%;
    padding: 14px 18px;
    border: 0;
    border-radius: 26px;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.96), rgba(248, 251, 255, 0.98));
    box-shadow: 0 12px 26px rgba(33, 49, 73, 0.08);
    color: #1d2a3a;
    text-align: left;
}

.search-entry-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    color: #7d8ea2;
    font-size: 14px;
}

.search-entry-placeholder {
    color: #415064;
    font-size: 15px;
    font-weight: 600;
}

.search-entry-hint {
    color: #93a1b3;
    font-size: 11px;
}

.home-overview-panel {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 10px;
    padding: 12px 0 2px;
}

.overview-stat {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 14px 12px;
    border-radius: 20px;
    background: rgba(255, 255, 255, 0.82);
    box-shadow: inset 0 0 0 1px rgba(31, 44, 61, 0.04);
    color: #6e7c8d;
}

.overview-stat strong {
    color: #243343;
    font-size: 20px;
    line-height: 1;
}

.overview-stat span {
    font-size: 12px;
}

.overview-filter-chip,
.overview-summary-copy {
    grid-column: 1 / -1;
}

.overview-filter-chip {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 38px;
    padding: 0 18px;
    border: 0;
    border-radius: 999px;
    background: rgba(5, 194, 203, 0.12);
    color: #057d86;
    font-size: 13px;
    font-weight: 600;
}

.overview-summary-copy {
    padding: 0 4px;
    color: #6e7b8d;
    font-size: 12px;
    line-height: 1.6;
}

.subpage-shell {
    min-height: calc(100vh - 24px);
    padding-top: calc(env(safe-area-inset-top) + 8px);
}

.subpage-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 48px;
    margin-bottom: 12px;
    padding: 0 4px;
}

.compact-back {
    min-width: 44px;
    padding-left: 0;
}

.placeholder-actions {
    min-width: 44px;
}

.search-hub-page {
    display: flex;
    flex-direction: column;
    gap: 16px;
    min-height: 100vh;
    min-height: 100dvh;
    margin: 0 calc(var(--album-page-side-pad) * -1);
    padding: calc(env(safe-area-inset-top) + 8px) 0 calc(28px + env(safe-area-inset-bottom));
    background: #fff;
}

.search-hub-page .subpage-header {
    min-height: 64px;
    margin: 0 0 10px;
    padding: 0 var(--album-page-side-pad);
    border-bottom: 1px solid #edf0f4;
}

.search-hub-page .search-hero-panel,
.search-hub-page .search-hub-shortcuts,
.search-hub-page .search-context-panel {
    margin-right: var(--album-page-side-pad);
    margin-left: var(--album-page-side-pad);
}

.search-reset-button {
    min-width: 44px;
    height: 34px;
    padding: 0 10px;
    border: 0;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.74);
    color: #6a7482;
    font-size: 13px;
    font-weight: 600;
}

.search-hero-panel {
    padding: 18px;
    border-radius: 28px;
    background:
        linear-gradient(145deg, rgba(255, 255, 255, 0.94), rgba(255, 251, 239, 0.9)),
        linear-gradient(135deg, rgba(5, 194, 203, 0.08), rgba(245, 163, 56, 0.1));
    box-shadow: 0 18px 36px rgba(85, 73, 45, 0.1);
}

.search-hero-copy {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 18px;
}

.search-hero-kicker {
    color: #d18a24;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.12em;
}

.search-hero-copy strong {
    max-width: 280px;
    color: #243343;
    font-size: 24px;
    font-weight: 800;
    line-height: 1.2;
}

.search-hero-copy p {
    margin: 0;
    color: #768291;
    font-size: 13px;
    line-height: 1.6;
}

.search-hub-input-shell {
    display: flex;
    align-items: center;
    gap: 10px;
    height: 50px;
    padding: 0 14px;
    border-radius: 18px;
    background: #fff;
    box-shadow: inset 0 0 0 1px rgba(64, 78, 98, 0.06), 0 10px 22px rgba(39, 54, 82, 0.08);
}

.search-hub-search-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: rgba(5, 194, 203, 0.1);
    color: #078c94;
    font-size: 13px;
    font-weight: 700;
}

.search-hub-input {
    flex: 1;
    min-width: 0;
    border: 0;
    background: transparent;
    color: #2b3543;
    font-size: 15px;
    font-weight: 600;
}

.search-hub-input::placeholder {
    color: #a6afbb;
}

.search-inline-clear {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    border: 0;
    border-radius: 50%;
    background: #eef2f6;
    color: #788393;
    font-size: 18px;
    line-height: 1;
}

.search-hub-shortcuts {
    display: grid;
    grid-template-columns: 1fr;
    gap: 12px;
}

.search-hub-shortcut {
    display: grid;
    grid-template-columns: 48px 1fr auto;
    align-items: center;
    gap: 14px;
    min-height: 92px;
    padding: 16px;
    border: 0;
    border-radius: 24px;
    background: rgba(255, 255, 255, 0.9);
    box-shadow: 0 16px 32px rgba(42, 54, 74, 0.08);
    text-align: left;
    position: relative;
    overflow: hidden;
}

.search-hub-shortcut::before {
    content: '';
    position: absolute;
    right: -28px;
    top: -42px;
    width: 120px;
    height: 120px;
    border-radius: 50%;
    opacity: 0.18;
}

.search-hub-shortcut--date::before {
    background: #f5a338;
}

.search-hub-shortcut--photo::before {
    background: #05c2cb;
}

.search-hub-shortcut--video::before {
    background: #4169e1;
}

.search-shortcut-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    border-radius: 18px;
    color: #fff;
    font-size: 16px;
    font-weight: 800;
}

.search-hub-shortcut--date .search-shortcut-icon {
    background: linear-gradient(135deg, #ffca65, #f3a43c);
}

.search-hub-shortcut--photo .search-shortcut-icon {
    background: linear-gradient(135deg, #5ee2d3, #05aeb8);
}

.search-hub-shortcut--video .search-shortcut-icon {
    background: linear-gradient(135deg, #7aa7ff, #4169e1);
}

.search-shortcut-copy {
    display: flex;
    flex-direction: column;
    gap: 5px;
    min-width: 0;
}

.search-hub-shortcut-label {
    color: #263342;
    font-size: 16px;
    font-weight: 800;
    line-height: 1;
}

.search-hub-shortcut-metric {
    color: #4d5b6d;
    font-size: 22px;
    font-weight: 800;
    line-height: 1;
}

.search-shortcut-copy small {
    color: #8a95a4;
    font-size: 12px;
    line-height: 1.4;
}

.search-shortcut-arrow {
    position: relative;
    z-index: 1;
    color: #bdc4ce;
    font-size: 28px;
    line-height: 1;
}

.search-context-panel {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
}

.search-context-item {
    min-height: 70px;
    padding: 14px;
    border-radius: 22px;
    background: rgba(255, 255, 255, 0.62);
    box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.66);
}

.search-context-item span {
    display: block;
    margin-bottom: 8px;
    color: #9aa3af;
    font-size: 12px;
}

.search-context-item strong {
    display: block;
    color: #4c5969;
    font-size: 13px;
    line-height: 1.45;
}

.calendar-weekdays {
    display: grid;
    grid-template-columns: repeat(7, minmax(0, 1fr));
    margin-bottom: 10px;
    color: #8a96a5;
    font-size: 12px;
    text-align: center;
}

.calendar-month-section + .calendar-month-section {
    margin-top: 18px;
}

.calendar-month-title {
    margin-bottom: 10px;
    color: #778493;
    font-size: 13px;
    font-weight: 600;
}

.calendar-grid {
    display: grid;
    grid-template-columns: repeat(7, minmax(0, 1fr));
    gap: 6px 0;
}

.calendar-cell {
    display: flex;
    justify-content: center;
    min-height: 54px;
}

.calendar-day {
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 38px;
    height: 48px;
    border: 0;
    border-radius: 18px;
    background: transparent;
    color: #c7ced7;
    font-size: 12px;
}

.calendar-day.active {
    color: #2b3442;
    font-weight: 600;
}

.calendar-day.today {
    background: #f9af15;
    color: #fff;
    font-weight: 700;
}

.calendar-day.today small {
    color: rgba(255, 255, 255, 0.92);
}

.calendar-day.selected:not(.today) {
    background: rgba(5, 194, 203, 0.12);
    color: #087d86;
}

.calendar-day small {
    margin-top: 2px;
    font-size: 10px;
}

.media-header-actions {
    min-width: 44px;
    justify-content: flex-end;
}

.media-header-text {
    color: #58687a;
    font-size: 14px;
}

.media-more-button {
    min-width: 44px;
    height: 44px;
    border: 0;
    background: transparent;
    color: #1c2531;
    font-size: 20px;
    line-height: 1;
}

.inline-upload-panel {
    margin-bottom: 14px;
}

.media-section + .media-section {
    margin-top: 20px;
}

.media-section-title {
    margin-bottom: 10px;
    color: #5b6777;
    font-size: 15px;
    font-weight: 600;
}

.media-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 2px;
}

.media-grid-item {
    position: relative;
    padding: 0;
    overflow: hidden;
    border: 0;
    background:
        linear-gradient(110deg, rgba(232, 237, 242, 0.96) 8%, rgba(247, 250, 251, 0.98) 18%, rgba(232, 237, 242, 0.96) 33%);
    background-size: 220% 100%;
    aspect-ratio: 1 / 1;
    animation: album-media-skeleton 1.15s ease-in-out infinite;
}

.media-grid-image {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
    opacity: 0;
    transform: scale(1.015);
    transition: opacity 0.22s ease, transform 0.32s ease;
}

.media-grid-image.loaded {
    opacity: 1;
    transform: scale(1);
}

.media-grid-item:has(.media-grid-image.loaded) {
    animation: none;
}

.media-play-indicator {
    position: absolute;
    top: 50%;
    left: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    margin: -17px 0 0 -17px;
    border-radius: 50%;
    background: rgba(17, 24, 35, 0.58);
    color: #fff;
    font-size: 15px;
    line-height: 1;
    text-indent: 2px;
    pointer-events: none;
}

.media-duration {
    position: absolute;
    right: 6px;
    bottom: 6px;
    padding: 2px 6px;
    border-radius: 999px;
    background: rgba(17, 24, 35, 0.72);
    color: #fff;
    font-size: 11px;
}

.batch-mode-item::after {
    content: '';
    position: absolute;
    inset: 0;
    background: rgba(15, 23, 33, 0.14);
}

.media-grid-item.selected::after {
    background: rgba(12, 156, 164, 0.18);
}

.media-select-indicator {
    position: absolute;
    top: 8px;
    right: 8px;
    z-index: 1;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    border: 2px solid rgba(255, 255, 255, 0.92);
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.72);
    color: transparent;
    font-size: 12px;
}

.media-grid-item.selected .media-select-indicator {
    border-color: #08a0a8;
    background: #08a0a8;
    color: #fff;
}

@keyframes album-media-skeleton {
    0% {
        background-position: 120% 0;
    }

    100% {
        background-position: -80% 0;
    }
}

.media-action-sheet {
    padding: 10px 12px 12px;
}

.sheet-action-button {
    width: 100%;
    height: 52px;
    border: 0;
    border-radius: 18px;
    background: #fff;
    color: #243343;
    font-size: 16px;
    font-weight: 600;
}

.sheet-action-button + .sheet-action-button {
    margin-top: 10px;
}

.sheet-action-button.danger {
    color: #d4544c;
}

.sheet-action-button.cancel {
    background: #f5f8fb;
    color: #5d6978;
}

.batch-delete-shell {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 31;
    padding: 0 12px calc(env(safe-area-inset-bottom) + 12px);
}

.batch-delete-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 14px;
    border-radius: 24px;
    background: rgba(255, 255, 255, 0.98);
    box-shadow: 0 -10px 32px rgba(24, 37, 52, 0.12);
}

.batch-delete-copy {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.batch-delete-copy strong {
    color: #213043;
    font-size: 14px;
}

.batch-delete-copy span {
    color: #778494;
    font-size: 12px;
}

.batch-delete-actions {
    display: flex;
    align-items: center;
    gap: 8px;
}

.batch-secondary,
.batch-primary {
    min-width: 70px;
    height: 38px;
    border: 0;
    border-radius: 999px;
    font-size: 13px;
    font-weight: 600;
}

.batch-secondary {
    background: #eff4f7;
    color: #5b6a7d;
}

.batch-primary {
    background: #15202f;
    color: #fff;
}

.batch-primary:disabled {
    opacity: 0.42;
}

.album-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 48px;
    padding: 0 8px;
    background: #fff;
    color: #333;
    border-radius: 18px 18px 0 0;
    border-bottom: 1px solid rgba(15, 23, 33, 0.06);
}

.wechat-browser-header {
    box-shadow: none;
}

.header-center {
    flex: 1;
    text-align: center;
}

.header-title {
    font-size: 17px;
    font-weight: 600;
    color: #333;
}

.header-side,
.header-date {
    border: 0;
    background: transparent;
    color: inherit;
}

.header-side {
    min-width: 44px;
    height: 44px;
    font-size: 16px;
}

.header-camera {
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 44px;
    height: 44px;
    padding: 0;
    border: 0;
    background: transparent;
    font-size: 22px;
    color: #333;
    cursor: pointer;
}

.header-back {
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 68px;
    height: 44px;
    padding: 0 8px;
    font-size: 16px;
    color: #4f6fa5;
    text-align: left;
}

.header-back-icon {
    width: 9px;
    height: 9px;
    border-left: 2px solid currentColor;
    border-bottom: 2px solid currentColor;
    transform: rotate(45deg);
}

.header-back-text {
    line-height: 1;
}

.header-actions {
    display: flex;
    align-items: center;
}

.header-search {
    color: #545866;
}

.header-plus-icon {
    font-size: 24px;
    line-height: 1;
    color: #12161d;
}

.header-date {
    font-size: 13px;
    color: rgba(18, 71, 79, 0.72);
}

.header-actions {
    display: flex;
    align-items: center;
    gap: 8px;
}

.album-toolbar {
    background: #fff;
    border-radius: 0 0 22px 22px;
    box-shadow: 0 10px 24px rgba(24, 37, 52, 0.08);
    overflow: hidden;
}

.native-nav-page .album-toolbar {
    border-radius: 22px;
}

.toolbar-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 14px 6px;
}

.toolbar-today {
    padding: 0 10px;
    border-radius: 999px;
    background: rgba(5, 194, 203, 0.1);
    color: #0a838a;
    font-weight: 600;
}

.filter-tabs {
    display: flex;
    align-items: center;
    border-top: 1px solid rgba(15, 23, 33, 0.06);
}

.filter-tab {
    flex: 1;
    height: 48px;
    padding: 0 16px;
    border: 0;
    background: transparent;
    color: #666;
    font-size: 15px;
    font-weight: 500;
    position: relative;
    transition: color 0.2s;
}

.filter-tab.active {
    color: #007aff;
    font-weight: 600;
}

.filter-tab.active::after {
    content: '';
    position: absolute;
    left: 50%;
    bottom: 0;
    width: 24px;
    height: 3px;
    background: #007aff;
    border-radius: 2px 2px 0 0;
    transform: translateX(-50%);
}

.search-panel {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 0 2px;
}

.search-input {
    flex: 1;
    height: 40px;
    padding: 0 14px;
    border: 0;
    border-radius: 16px;
    background: rgba(255, 255, 255, 0.92);
    color: #2a2f3a;
    font-size: 14px;
}

.search-clear {
    width: 52px;
    height: 40px;
    border: 0;
    border-radius: 16px;
    background: rgba(255, 255, 255, 0.88);
    color: #0b7b83;
    font-size: 13px;
}

.upload-progress-panel {
    margin-bottom: 14px;
    padding: 12px 14px 14px;
    border-radius: 18px;
    background: rgba(255, 251, 240, 0.96);
    box-shadow: inset 0 0 0 1px rgba(243, 170, 65, 0.12);
    color: #7f5f1f;
}

.mode-banner {
    margin-bottom: 14px;
    padding: 10px 12px;
    border-radius: 18px;
    background: rgba(255, 255, 255, 0.88);
    box-shadow: 0 10px 24px rgba(59, 74, 94, 0.08);
    color: #4d5f77;
    font-size: 12px;
    line-height: 1.6;
}

.upload-progress-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 8px;
}

.upload-progress-head strong {
    color: #8d5d18;
    font-size: 13px;
    font-weight: 700;
}

.upload-progress-count {
    min-width: 52px;
    text-align: right;
    color: #6b4f1d;
    font-size: 13px;
    font-weight: 600;
}

.upload-progress-track {
    margin-bottom: 6px;
}

.upload-progress-bar {
    background: linear-gradient(90deg, #ffcf63, #f19a35);
}

.upload-progress-note {
    margin: 0;
    color: #9b7d4f;
    font-size: 12px;
    line-height: 1.5;
}

.progress-track {
    height: 6px;
    overflow: hidden;
    border-radius: 999px;
    background: rgba(127, 95, 31, 0.14);
}

.progress-bar {
    height: 100%;
    border-radius: inherit;
    background: linear-gradient(90deg, #ffc44e, #f39a36);
}

.status-state {
    padding: 32px 20px;
    border-radius: 28px;
    background: rgba(255, 255, 255, 0.82);
    text-align: center;
    box-shadow: 0 18px 36px rgba(59, 74, 94, 0.08);
}

.status-title {
    margin-bottom: 8px;
    color: #243343;
    font-size: 18px;
    font-weight: 600;
}

.status-state p {
    margin: 0 0 18px;
    color: #718193;
    font-size: 13px;
    line-height: 1.6;
}

.error-state {
    background: rgba(255, 250, 245, 0.9);
}

.empty-state {
    padding: 48px 20px;
    border-radius: 28px;
    background: rgba(255, 255, 255, 0.76);
    text-align: center;
    box-shadow: 0 18px 36px rgba(59, 74, 94, 0.08);
}

.empty-icon {
    font-size: 64px;
    margin-bottom: 16px;
}

.empty-state h2 {
    margin: 0 0 8px;
    color: #243343;
    font-size: 18px;
}

.empty-state p {
    margin: 0 0 18px;
    color: #718193;
    font-size: 13px;
    line-height: 1.6;
}

.empty-action {
    height: 38px;
    padding: 0 18px;
    border: 0;
    border-radius: 999px;
    background: #05c2cb;
    color: #fff;
    font-size: 14px;
    font-weight: 600;
}

.album-group + .album-group {
    margin-top: 18px;
}

.group-header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 12px;
    padding: 0 4px;
}

.group-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #efb33f;
    box-shadow: 0 0 0 4px rgba(239, 179, 63, 0.16);
}

.group-title {
    display: flex;
    align-items: baseline;
    gap: 8px;
    color: #425262;
}

.group-title strong {
    font-size: 16px;
}

.group-title span {
    color: #7d8795;
    font-size: 12px;
}

.album-card-wrap + .album-card-wrap {
    margin-top: 12px;
}

.overlay-mask {
    position: fixed;
    inset: 0;
    z-index: 30;
    display: flex;
    align-items: flex-end;
    padding: 18px 12px calc(18px + env(safe-area-inset-bottom));
    background: rgba(16, 22, 35, 0.34);
}

.bottom-sheet {
    width: 100%;
    max-height: 78vh;
    overflow-y: auto;
    padding: 18px;
    border-radius: 26px;
    background: rgba(255, 255, 255, 0.96);
    box-shadow: 0 20px 44px rgba(18, 31, 48, 0.2);
    backdrop-filter: blur(18px);
}

.sheet-handle {
    width: 42px;
    height: 5px;
    margin: -4px auto 14px;
    border-radius: 999px;
    background: rgba(76, 89, 108, 0.18);
}

.sheet-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 16px;
}

.sheet-head h3 {
    margin: 0 0 6px;
    color: #223140;
    font-size: 18px;
}

.sheet-head p {
    margin: 0;
    color: #7b8796;
    font-size: 12px;
    line-height: 1.6;
}

.sheet-close {
    min-width: 48px;
    height: 32px;
    border: 0;
    border-radius: 999px;
    background: #eef3f7;
    color: #556575;
    font-size: 12px;
}

.create-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 12px;
}

.create-hero {
    margin-bottom: 14px;
    padding: 18px;
    border-radius: 24px;
    background: linear-gradient(135deg, rgba(255, 246, 209, 0.96), rgba(227, 249, 246, 0.98));
}

.create-hero-copy {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.create-hero-kicker {
    display: inline-flex;
    width: fit-content;
    padding: 4px 10px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.76);
    color: #5a6b7d;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.04em;
}

.create-hero-copy strong {
    color: #223140;
    font-size: 18px;
    line-height: 1.5;
}

.create-hero-copy p {
    margin: 0;
    color: #5f7083;
    font-size: 13px;
    line-height: 1.7;
}

.create-item {
    display: grid;
    grid-template-columns: auto 1fr auto;
    align-items: center;
    gap: 12px;
    padding: 18px 16px;
    border: 1px solid rgba(18, 33, 49, 0.06);
    border-radius: 22px;
    background: #f9fbfc;
    text-align: left;
}

.create-item--smart-upload {
    background: linear-gradient(135deg, rgba(239, 246, 255, 0.96), rgba(247, 251, 255, 0.98));
    border-color: rgba(90, 138, 255, 0.16);
}

.create-item--camera {
    background: linear-gradient(135deg, rgba(255, 247, 238, 0.98), rgba(255, 252, 247, 0.98));
}

.create-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    border-radius: 16px;
    color: #fff;
    font-size: 16px;
    font-weight: 700;
}

.create-icon-shell {
    display: inline-flex;
    padding: 6px;
    border-radius: 18px;
    background: rgba(255, 255, 255, 0.92);
    box-shadow: inset 0 0 0 1px rgba(18, 33, 49, 0.04);
}

.create-copy {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.create-item strong {
    color: #223140;
    font-size: 15px;
}

.create-copy span {
    color: #768395;
    font-size: 12px;
    line-height: 1.5;
}

.create-arrow {
    color: #8ea0b5;
    font-size: 22px;
    line-height: 1;
}

.composer-tabs {
    display: flex;
    gap: 8px;
    margin-bottom: 14px;
}

.tab-chip {
    flex: 1;
    height: 36px;
    border: 0;
    border-radius: 999px;
    background: #eff4f7;
    color: #5e6c7d;
    font-size: 13px;
}

.tab-chip.active {
    background: rgba(5, 194, 203, 0.14);
    color: #038791;
    font-weight: 600;
}

.sample-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 8px;
    margin-bottom: 12px;
}

.sample-image {
    position: relative;
    padding: 0;
    overflow: hidden;
    border: 0;
    border-radius: 16px;
    background: #eff4f7;
    aspect-ratio: 1 / 1;
}

.sample-image img,
.candidate-card img,
.video-frame img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.sample-badge {
    position: absolute;
    right: 8px;
    bottom: 8px;
    padding: 4px 8px;
    border-radius: 999px;
    background: rgba(18, 24, 35, 0.74);
    color: #fff;
    font-size: 11px;
}

.media-preview {
    border: 1px solid rgba(5, 194, 203, 0.16);
}

.asset-remove {
    position: absolute;
    top: 8px;
    left: 8px;
    min-width: 42px;
    height: 26px;
    border: 0;
    border-radius: 999px;
    background: rgba(16, 22, 35, 0.66);
    color: #fff;
    font-size: 12px;
}

.asset-placeholder {
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-bottom: 12px;
    padding: 18px 16px;
    border-radius: 18px;
    background: #f6fafb;
    color: #6f7d8d;
}

.asset-placeholder strong {
    color: #223140;
    font-size: 14px;
}

.asset-placeholder span {
    font-size: 12px;
    line-height: 1.6;
}

.video-fallback {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    color: #546475;
    font-size: 14px;
    font-weight: 600;
}

.secondary-action,
.primary-action {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 42px;
    border: 0;
    border-radius: 16px;
    font-size: 14px;
    font-weight: 600;
}

.secondary-action {
    margin-bottom: 12px;
    background: #eff4f7;
    color: #556575;
}

.secondary-action.compact {
    margin: 10px 0 0;
}

.primary-action {
    margin-top: 12px;
    background: #05c2cb;
    color: #fff;
    box-shadow: 0 12px 24px rgba(5, 194, 203, 0.24);
}

.primary-action:disabled {
    opacity: 0.48;
    box-shadow: none;
}

.composer-input,
.composer-textarea {
    width: 100%;
    padding: 12px 14px;
    border: 0;
    border-radius: 16px;
    background: #f5f8fb;
    color: #263546;
    font-size: 14px;
}

.composer-input + .composer-input,
.composer-input + .primary-action,
.composer-textarea + .composer-input,
.composer-textarea + .composer-textarea {
    margin-top: 12px;
}

.composer-textarea {
    resize: none;
    line-height: 1.6;
}

.candidate-summary {
    margin-bottom: 12px;
    color: #6e7b8d;
    font-size: 12px;
    line-height: 1.6;
}

.candidate-list {
    display: grid;
    gap: 10px;
}

.smart-upload-shell {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.smart-upload-hero {
    display: flex;
    flex-direction: column;
    gap: 14px;
    padding: 18px;
    border-radius: 22px;
    background: linear-gradient(135deg, rgba(238, 245, 255, 0.98), rgba(246, 249, 255, 0.98));
}

.smart-upload-copy {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.smart-upload-kicker {
    display: inline-flex;
    width: fit-content;
    padding: 4px 10px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.8);
    color: #5371a6;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.04em;
}

.smart-upload-copy strong {
    color: #213043;
    font-size: 18px;
}

.smart-upload-copy p {
    margin: 0;
    color: #63758b;
    font-size: 13px;
    line-height: 1.7;
}

.smart-upload-trigger {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 42px;
    border: 0;
    border-radius: 16px;
    background: #1f2c3d;
    color: #fff;
    font-size: 14px;
    font-weight: 600;
}

.smart-upload-stats {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 10px;
}

.smart-stat-card {
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 14px 12px;
    border-radius: 18px;
    background: #f7fafc;
}

.smart-stat-card span {
    color: #738293;
    font-size: 11px;
}

.smart-stat-card strong {
    color: #213043;
    font-size: 20px;
    line-height: 1;
}

.smart-summary-card {
    margin-bottom: 0;
    padding: 14px 16px;
    border-radius: 18px;
    background: #f7fafc;
}

.smart-selection-toolbar {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.smart-selection-count {
    color: #223140;
    font-size: 13px;
    font-weight: 600;
}

.smart-selection-actions {
    display: flex;
    gap: 8px;
}

.smart-selection-pill {
    flex: 1;
    height: 34px;
    border: 0;
    border-radius: 999px;
    background: rgba(108, 167, 255, 0.14);
    color: #3764b0;
    font-size: 12px;
    font-weight: 600;
}

.smart-selection-pill.muted {
    background: #eef3f7;
    color: #59697b;
}

.smart-candidate-list {
    gap: 12px;
}

.candidate-card {
    display: grid;
    grid-template-columns: 88px 1fr;
    align-items: start;
    gap: 12px;
    padding: 12px;
    border-radius: 18px;
    background: #f7fafb;
    border: 0;
    text-align: left;
}

.candidate-card img {
    border-radius: 14px;
    aspect-ratio: 1 / 1;
}

.smart-candidate-card {
    position: relative;
    background: #fbfcfd;
    box-shadow: inset 0 0 0 1px rgba(18, 33, 49, 0.06);
}

.smart-candidate-card.selected {
    box-shadow: inset 0 0 0 2px rgba(5, 194, 203, 0.28);
    background: rgba(242, 252, 252, 0.98);
}

.candidate-media {
    position: relative;
}

.candidate-select-chip,
.candidate-type-chip {
    position: absolute;
    left: 8px;
    padding: 4px 8px;
    border-radius: 999px;
    font-size: 10px;
    font-weight: 600;
}

.candidate-select-chip {
    top: 8px;
    background: rgba(16, 22, 35, 0.68);
    color: #fff;
}

.candidate-type-chip {
    bottom: 8px;
    background: rgba(255, 255, 255, 0.9);
    color: #51637a;
}

.candidate-type-chip.recommended {
    background: rgba(255, 244, 214, 0.96);
    color: #9c6b18;
}

.candidate-title-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
}

.candidate-media-tag {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 40px;
    height: 22px;
    padding: 0 8px;
    border-radius: 999px;
    background: #eef3f7;
    color: #59697b;
    font-size: 11px;
    font-weight: 600;
}

.candidate-copy {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.candidate-copy strong {
    color: #253444;
    font-size: 14px;
}

.candidate-copy span {
    color: #738293;
    font-size: 12px;
}

.candidate-reason-badge {
    display: inline-flex;
    width: fit-content;
    margin-top: 4px;
    padding: 6px 10px;
    border-radius: 12px;
    background: rgba(5, 194, 203, 0.1);
    color: #067981;
    line-height: 1.5;
}

.candidate-duplicate-note {
    color: #8c6b2a;
}

.smart-empty-state {
    margin-bottom: 0;
}

.sort-panel {
    margin-bottom: 12px;
    padding: 14px;
    border-radius: 18px;
    background: #f7fafb;
}

.sort-head {
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-bottom: 12px;
}

.sort-head strong {
    color: #223140;
    font-size: 14px;
}

.sort-head span {
    color: #738293;
    font-size: 12px;
    line-height: 1.5;
}

.sort-grid {
    display: grid;
    gap: 10px;
}

.sort-item {
    display: grid;
    grid-template-columns: 96px 1fr;
    gap: 10px;
    align-items: center;
}

.sort-media {
    position: relative;
    overflow: hidden;
    border-radius: 16px;
    aspect-ratio: 1 / 1;
    background: #eff4f7;
}

.sort-media img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.sort-index {
    position: absolute;
    top: 8px;
    left: 8px;
    min-width: 24px;
    height: 24px;
    padding: 0 6px;
    border-radius: 999px;
    background: rgba(18, 24, 35, 0.74);
    color: #fff;
    font-size: 12px;
    line-height: 24px;
    text-align: center;
}

.sort-actions {
    display: flex;
    gap: 8px;
}

.sort-button {
    flex: 1;
    height: 34px;
    border: 0;
    border-radius: 14px;
    background: #e8eef5;
    color: #536174;
    font-size: 12px;
    font-weight: 600;
}

.sort-button:disabled {
    opacity: 0.42;
}

.video-mask {
    align-items: center;
    justify-content: center;
    padding: 0;
    background: #000;
}

.video-preview {
    position: relative;
    width: 100%;
    height: 100%;
    padding: 0;
    border-radius: 0;
    background: #000;
    color: #fff;
}

.video-close {
    position: absolute;
    top: calc(14px + env(safe-area-inset-top));
    right: 14px;
    z-index: 1;
    width: 52px;
    height: 32px;
    border: 0;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.12);
    color: #fff;
    font-size: 12px;
}

.video-element,
.video-preview-poster {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: contain;
    background: #000;
}

.video-play {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 64px;
    height: 64px;
    margin: -32px 0 0 -32px;
    border: 0;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.16);
    color: #fff;
    font-size: 24px;
}

.video-preview-play {
    position: absolute;
    top: 50%;
    left: 50%;
    z-index: 2;
    width: 74px;
    height: 74px;
    margin: -37px 0 0 -37px;
    border: 0;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.18);
    color: #fff;
    font-size: 30px;
    line-height: 74px;
    text-indent: 4px;
    backdrop-filter: blur(8px);
}

.video-length {
    position: absolute;
    right: 12px;
    bottom: calc(16px + env(safe-area-inset-bottom));
    padding: 4px 8px;
    border-radius: 999px;
    background: rgba(16, 22, 32, 0.68);
    font-size: 12px;
}

.video-copy {
    position: absolute;
    left: 16px;
    right: 78px;
    bottom: calc(16px + env(safe-area-inset-bottom));
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-top: 0;
    pointer-events: none;
}

.video-copy strong {
    font-size: 16px;
    line-height: 1.5;
}

.video-copy span {
    color: rgba(255, 255, 255, 0.72);
    font-size: 12px;
}

.search-fade-enter-active,
.search-fade-leave-active,
.sheet-fade-enter-active,
.sheet-fade-leave-active {
    transition: all 0.2s ease;
}

.search-fade-enter,
.search-fade-leave-to,
.sheet-fade-enter,
.sheet-fade-leave-to {
    opacity: 0;
    transform: translateY(8px);
}

.composer-file-input {
    position: absolute;
    width: 0;
    height: 0;
    opacity: 0;
    pointer-events: none;
}

.file-picker-action {
    position: relative;
    overflow: hidden;
}

.composer-file-input--cover {
    inset: 0;
    width: 100%;
    height: 100%;
    pointer-events: auto;
}

@media (max-width: 374px) {
    .smart-upload-stats {
        grid-template-columns: 1fr;
    }

    .smart-selection-actions {
        flex-direction: column;
    }
}
</style>

<style lang="scss">
html.baby-album-scroll-page,
body.baby-album-scroll-page,
#app.baby-album-scroll-page {
    background: #f6f3ee !important;
}

#app.baby-album-scroll-page {
    -webkit-overflow-scrolling: touch;
}
</style>
